<script>
  import { getContext, onMount, onDestroy } from "svelte";
  import { useNavigate } from "svelte-navigator";
  import { fetchWithSession } from "../../../lib/util/fetchWithSession.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import Header from "../../../lib/components/Header.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { ROUTES } from "@utils/routes.js";
  import { ComboBox, MultiSelect, Tag } from "carbon-components-svelte";
  import { Modal as DangerModal } from "carbon-components-svelte";
  import Modal from "@components/Modal.svelte";
  import ContentSwitcher from "@components/ContentSwitcher.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import ProjectStepActions from "./components/ProjectStepActions.svelte";
  import ImportFrameworkModal from "./components/ImportFrameworkModal.svelte";
  import ExistingFrameworkDetectedModal from "./components/ExistingFrameworkDetectedModal.svelte";
  import CompanyInfo from "./components/CompanyInfo.svelte";
  import OrganizationFramework from "./components/OrganizationFramework.svelte";
  import ProcessFramework from "./components/ProcessFramework.svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import FrameworkInfoRow from "./components/FrameworkInfoRow.svelte";
  import {
    InfoBlock,
    Select,
    SelectItem,
    TextInput,
    SectionWrapper,
    Button,
  } from "paxray-components-svelte";
  import { ROBOT_2_ICON, RESET_ICON } from "@utils/icons.js";

  const urlParams = useUrlParams();
  const role = getContext("role");
  const navigate = useNavigate();
  const step = PROJECT_STAGES.SETUP.steps.CONFIGURE;
  const stage = PROJECT_STAGES.SETUP.id;

  let deptRows = [];
  let processRows = [];
  let objectRows = [];
  let subprocessesRows = [];
  let taskRows = [];
  let subtaskRows = [];
  let collectionSelectedCount = 0;
  let collectionStorageError = "";
  let collectionProjectId;
  let collectionReady = false;
  let collectionSaving = false;
  let openImportModal = false;
  let openExistingFrameworkModal = false;
  let displayTableOptions = [];
  let replaceFramework = false;
  let frameworkDataLoaded = false;
  let impactSummary = {
    processes: 0,
    subprocesses: 0,
    tasks: 0,
    subtasks: 0,
  };
  let tableViewOptions = [
    "All",
    "Departments",
    "Processes",
    "Subprocesses",
    "Tasks",
    "Subtasks",
  ];
  const companyStructureOptions = [
    "Company Info",
    "Orga Framework",
    "Process Framework",
  ];
  const companyStructureRoutes = [
    ROUTES.dam.companySettings.companyInfo,
    ROUTES.dam.companySettings.organizationFramework,
    ROUTES.dam.companySettings.processFramework,
  ];
  const companyInfoStorageKey = "paxray.company-info";
  const defaultCompanyInfo = () => ({
    name: "",
    headquarters: "",
    industry: "",
    industries: [],
    companySize: "",
    businessModel: "",
    businessModels: [],
    businessAreas: "",
    employeeCount: "",
    description: "",
    productsAndServices: "",
    customerSegments: "",
    operatingRegions: "",
    coreSystems: "",
    strategicPriorities: "",
    currentChallenges: "",
    preferredTerminology: "",
    constraints: "",
    useInAiAnalyses: true,
    useOrganizationInfoInAiAnalyses: true,
    useContextObjectivesInAiAnalyses: true,
    regions: [],
    offerings: [],
    customerSegmentsSelected: [],
    strategicPrioritiesSelected: [],
    keyChallenges: [],
    restrictedAssumptions: [],
    systemsTools: [],
    customNote: "",
  });
  const companyInfoChoices = {
    industry: [
      "Financial Services",
      "Healthcare",
      "Manufacturing",
      "Retail",
      "Public Sector",
      "Technology",
      "Other",
    ],
    companySize: [
      "<250",
      "251-1.000",
      "1.001-5.000",
      "5.001-25.000",
      "25.000+",
    ],
    businessModel: [
      "Digital Transformation",
      "Process Optimization",
      "Operational Excellence",
      "Automation",
      "Cost Reduction",
      "Customer Experience",
      "Compliance & Risk",
      "Innovation",
      "Other",
    ],
    regions: [
      "North America",
      "Europe",
      "Asia Pacific",
      "Latin America",
      "Middle East",
      "Africa",
      "Other",
    ],
    offerings: [
      "Process Excellence",
      "Operations",
      "IT",
      "Shared Services",
      "Business Units",
      "Management",
      "Consulting / External",
      "Other",
    ],
    customerSegments: [
      "Mid-market",
      "Enterprise",
      "SMB",
      "Public Sector",
      "Non-Profit",
      "B2B",
      "B2C",
      "Other",
    ],
    strategicPriorities: [
      "Identify automation potential",
      "Understand process variants",
      "Reduce cycle times",
      "Improve quality & reduce errors",
      "Increase standardization",
      "Support transformation",
      "Build business case",
      "Other",
    ],
    keyChallenges: [
      "Lack of process transparency",
      "High manual effort",
      "Too many system handoffs",
      "Inconsistent ways of working",
      "Long cycle times",
      "High error rates",
      "Poor data quality",
      "Hard to prioritize automation",
      "Other",
    ],
    targetAreas: [
      "Finance",
      "Customer Service",
      "HR",
      "Procurement",
      "Sales",
      "IT Service Management",
      "Backoffice",
      "Production",
      "Administration",
      "Other",
    ],
  };
  let selectedCompanyStructureView = 0;
  let companyInfo = defaultCompanyInfo();
  let companyInfoLoaded = false;
  let companyInfoSaveTimer;
  let orgFrameworkRows = [];
  let visibleOrgFrameworkRows = [];
  let orgFrameworkLoading = false;
  let orgFrameworkError = "";
  let collapsedOrgBusinessUnits = {};
  let orgBusinessUnits = [];
  let orgDepartments = [];
  let orgAreas = [];
  let orgFilteredFrameworkRows = [];
  let orgFrameworkPagedRows = [];
  let orgFrameworkPage = 1;
  let orgFrameworkPageSize = 25;
  let orgFrameworkPageCount = 1;
  let selectedOrgBusinessUnit = "";
  let selectedOrgDepartment = "";
  let selectedOrgArea = "";
  let orgFilterKey = 0;
  let openOrgModal = false;
  let openOrgDangerModal = false;
  let orgModalMode = "add";
  let orgModalType = "";
  let orgModalName = "";
  let orgModalParentId = "";
  let selectedOrgObject = null;
  let paginations = {
    Departments: { page: 1, pageSize: 10 },
    Processes: { page: 1, pageSize: 10 },
    Objects: { page: 1, pageSize: 10 },
    Subprocesses: { page: 1, pageSize: 10 },
    Tasks: { page: 1, pageSize: 10 },
    Subtasks: { page: 1, pageSize: 10 },
  };
  let companyFrameworkRows = [];
  let filteredCompanyFrameworkRows = [];
  let visibleCompanyFrameworkRows = [];
  let companyFrameworkPagedRows = [];
  let companyFrameworkPage = 1;
  let companyFrameworkPageSize = 25;
  let companyFrameworkPageCount = 1;
  let companyFrameworkSearch = "";
  let companyFrameworkCompleteness = "";
  let companyFrameworkSortKey = "";
  let companyFrameworkSortDirection = "none";
  let companyQuickError = "";
  let collapsedCompanyDepartments = {};
  let companyQuickDepartment = "";
  let companyQuickDepartmentId = "";
  let companyQuickProcess = "";
  let companyQuickProcessId = "";
  let companyQuickSubprocess = "";
  let companyQuickSubprocessId = "";
  let companyQuickTask = "";
  let companyQuickTaskId = "";
  let companyQuickSubtask = "";
  let companyQuickSaving = false;

  let searchTerms = {
    Departments: "",
    Processes: "",
    Objects: "",
    Subprocesses: "",
    Tasks: "",
    Subtasks: "",
  };

  let deptHeaders = [
    { key: "name", value: "Department" },
    { key: "projects", value: "Projects" },
    { key: "actions", value: "New Process", width: "150px" },
    { key: "edit", value: "Edit", width: "100px" },
    { key: "delete", value: "Delete", width: "100px" },
  ];

  $: processHeaders = [
    { key: "name", value: "Process" },
    selectedProcessView === 1
      ? { key: "objects", value: "Objects" }
      : { key: "departments", value: "Departments" },
    { key: "actions", value: "New Subprocess", width: "150px" },
    { key: "edit", value: "Edit", width: "100px" },
    { key: "delete", value: "Delete", width: "100px" },
  ];

  let objectHeaders = [
    { key: "name", value: "Object" },
    { key: "edit", value: "Edit", width: "100px" },
    { key: "delete", value: "Delete", width: "100px" },
  ];

  let subprocessesHeaders = [
    { key: "name", value: "Subprocess" },
    { key: "process", value: "Process" },
    { key: "actions", value: "New Task", width: "150px" },
    { key: "edit", value: "Edit", width: "100px" },
    { key: "delete", value: "Delete", width: "100px" },
  ];

  let taskHeaders = [
    { key: "name", value: "Task" },
    { key: "subprocess", value: "Subprocess" },
    { key: "actions", value: "New Subtask", width: "150px" },
    { key: "edit", value: "Edit", width: "100px" },
    { key: "delete", value: "Delete", width: "100px" },
  ];

  let subtaskHeaders = [
    { key: "name", value: "Subtask" },
    { key: "task", value: "Task" },
    { key: "edit", value: "Edit", width: "100px" },
    { key: "delete", value: "Delete", width: "100px" },
  ];

  let selectedObject = null;
  let selectedType = "";

  let openDangerModal = false;
  let openModalAdd = false;

  let newName = "";
  let previousToSelect = [];
  let nextToSelect = [];
  let selectedPrevIds = [];
  let selectedPrevId = "";
  let selectedNextIds = [];
  let selectedAddType = "";
  let isEditMode = false;
  let isSubordinateMode = false;
  let frameworkNameValidation = { duplicate: false };
  let frameworkModalSaving = false;
  let frameworkModalError = "";
  let frameworkDeleteSaving = false;
  let frameworkDeleteError = "";
  let openCompanyMissingPathModal = false;
  let companyMissingPathTarget = "";
  let companyMissingPathRow = null;
  let companyMissingPathNames = {};
  let companyMissingPathSaving = false;
  let companyMissingPathError = "";
  let companyMissingPathLevels = [];
  let companyMissingPathSubtitle = "";
  let typeOptions = [
    { id: "Departments", text: "Department" },
    { id: "Processes", text: "Process" },
    { id: "Objects", text: "Object" },
    { id: "Subprocesses", text: "Subprocess" },
    { id: "Tasks", text: "Task" },
    { id: "Subtasks", text: "Subtask" },
  ];

  let selectedProcessView = 0;
  let showLegacyProjectFramework = false;
  let frameworkTourMounted = false;
  let unregisterFrameworkTour;
  let showFrameworkGuide = false;
  let frameworkGuideStep = 0;
  let frameworkGuideSteps = [];

  onMount(() => {
    frameworkTourMounted = true;
    return () => unregisterFrameworkTour?.();
  });

  $: if (
    frameworkTourMounted &&
    idproject &&
    !showLegacyProjectFramework &&
    frameworkDataLoaded &&
    !orgFrameworkLoading
  ) {
    if (!unregisterFrameworkTour)
      unregisterFrameworkTour = registerViewTour(startFrameworkGuide);
  } else {
    unregisterFrameworkTour?.();
    unregisterFrameworkTour = null;
    showFrameworkGuide = false;
  }

  function startFrameworkGuide() {
    const scope = ".project-framework-new .company-framework-content";
    frameworkGuideSteps = [
      {
        target:
          ".project-framework-new .project-framework-intro .warning-notice.hint",
        title: "Your framework at a glance",
        text: "This overview shows the number of processes, subprocesses, tasks and subtasks in the framework below. These totals describe the framework, not your collection selection.",
      },
      {
        target: '[data-guide="project-framework-assistant"] > button',
        title: "Build your framework with guided help",
        text: 'Use "Start Framework Assistant" on the right of the green overview to start the guided setup. If an assistant draft already exists, you will be asked to replace it. Cancel keeps the draft and leaves you on this page. Your saved project framework stays unchanged when starting the setup.',
      },
      {
        target: `${scope} .company-framework-table`,
        title: "Explore your project framework",
        text: "Review the framework from departments through processes, subprocesses and tasks to individual subtasks. Expand a department using its name or the arrow. Green rows indicate subtasks selected for collection.",
      },
      {
        target: `${scope} .company-framework-filter-bar`,
        title: "Find relevant activities",
        text: "Search for activities or filter by department, process, subprocess and task. Use the completeness filter to find incomplete paths. Reset the filters to show the full framework again; filtering does not change your collection selection.",
      },
      {
        target: `${scope} .company-framework-missing`,
        title: "Complete missing framework entries",
        text: 'Red "No …" labels show missing levels in a framework path. Click one to add the missing entries. A "Missing" badge in a department summary indicates incomplete paths within that department.',
      },
      {
        target: `${scope} .company-framework-table thead`,
        title: "Add, edit or remove entries",
        text: 'Use the plus icons in the column headings to add entries. Expand a department and hover over a detail row to reveal buttons for adding child entries, editing and deleting. Deleting removes framework data; to exclude a subtask from collection, clear its "In Scope" checkbox instead.',
      },
      {
        target: `${scope} th.collection-column`,
        title: "Select individual subtasks",
        text: 'Expand a department and use the checkboxes in the "In Scope" column to select individual subtasks for this project\'s data collection. Changes are saved automatically. At least one subtask must be selected before you can complete setup.',
      },
      {
        target: `${scope} .company-framework-group .bx--checkbox-wrapper`,
        title: "Select entire groups",
        text: 'Use a checkbox beside a department, process, subprocess or task to select all its subtasks, including those hidden by filters, collapsed groups or pagination. The department\'s "In Scope" badge shows "All" or the selected share.',
      },
      {
        target:
          '[data-guide="complete-project-framework"] > button, [data-guide="complete-project-framework"] > .completed-badge',
        title: "Complete and lock your framework",
        text: 'Review the framework and your collection selection, then choose "Complete & Lock Framework". The button becomes available once at least one subtask is selected and all selection changes have been saved successfully. Confirming locks the framework settings and cannot be undone. Afterwards, "Completed" appears here.',
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    frameworkGuideStep = 0;
    showFrameworkGuide = frameworkGuideSteps.length > 0;
  }

  // Configure Framework zeigt den Stand (Infozeile + Tabelle); aufgebaut wird im Framework Assistant, gefuehrt und mit
  // den Team-Umfragen. Der Assistent wird ueber den Button geoeffnet.
  let projectDepartments = [];
  let openReplaceAssistantDraftModal = false;
  const assistantDraftKey = "paxray.framework-assistant.draft"; // wie draftStorageKey in FrameworkAssistantChat.svelte

  function openGuidedAssistant() {
    try {
      if (localStorage.getItem(assistantDraftKey)) {
        openReplaceAssistantDraftModal = true;
        return;
      }
    } catch {
      // Ohne Browserspeicher startet der Assistent bei der Abteilungswahl
    }
    startGuidedAssistant();
  }

  function startGuidedAssistant() {
    openReplaceAssistantDraftModal = false;
    try {
      const single = projectDepartments.length === 1;
      localStorage.setItem(
        assistantDraftKey,
        JSON.stringify({
          step: 1,
          generationMode: "bottom_up",
          started: true,
          selectedAreas: single ? projectDepartments : [],
        }),
      );
    } catch {
      // Ohne Browserspeicher startet der Assistent bei der Abteilungswahl
    }
    // Projekt im Anker, nicht als ?idproject - sonst bleibt diese Seite in App.svelte zusaetzlich eingehaengt
    navigate(`${ROUTES.frameworkAssistant}#project=${idproject}`);
  }

  function frameworkInfoLoaded(event) {
    projectDepartments = event.detail.departments;
  }
  let selectedTableView = 1;
  let projects = [];
  let selectedDepartment = "";
  let selectedProcess = "";
  let selectedSubprocess = "";
  let selectedTask = "";
  let selectedSubtask = "";
  let filterKey = 0;

  $: ({ idproject } = $urlParams);
  $: if (collectionProjectId !== idproject) {
    collectionProjectId = idproject;
    collectionReady = false;
    collectionSaving = false;
    collectionSelectedCount = 0;
    collectionStorageError = "";
  }
  $: if (!idproject) {
    const routeIndex = companyStructureRoutes.indexOf($urlParams.pathname);
    selectedCompanyStructureView = routeIndex >= 0 ? routeIndex : 0;
  }

  $: projectHasDepartment = Boolean(
    idproject &&
      deptRows.some((department) =>
        (department.projects || []).some(
          (project) => String(project.id) === String(idproject),
        ),
      ),
  );
  $: departmentAssignmentMissing = Boolean(idproject && !projectHasDepartment);
  // Beim Anlegen gewaehlte Departments - ProcessFramework uebernimmt deren Subtasks in einen leeren Scope
  $: projectDepartmentRows = idproject
    ? deptRows.filter((department) =>
        (department.projects || []).some(
          (project) => String(project.id) === String(idproject),
        ),
      )
    : [];
  $: projectDepartmentIds = projectDepartmentRows.map((department) =>
    String(department.id),
  );
  // Framework-Elemente gehoeren zum Department, Aenderungen im Projekt wirken auf alle Projekte dieses Departments
  $: departmentFrameworkNotice = projectDepartmentRows.length
    ? `Changes are saved to the framework of ${projectDepartmentRows.map((department) => department.name).join(", ")} and apply to all projects in this department.`
    : "";

  // Im Projekt strikt: nur Processes, die mit einem Projekt-Department verknuepft sind (ohne Department: leer)
  $: projectScopeActive = Boolean(idproject);
  // Wie groupLevel in ProcessFramework: im Projekt nach Process gruppiert, Department ausgeblendet
  $: frameworkGroupLevel = idproject ? "process" : "department";
  $: listOfDepartments = populateList(
    projectScopeActive
      ? deptRows.filter((department) =>
          projectDepartmentIds.includes(String(department.id)),
        )
      : deptRows,
  );
  // Filterlisten im Projekt auf die Eintraege der Projekt-Departments beschraenken
  $: inProjectScope = (type, rows) =>
    projectScopeActive
      ? rows.filter((row) => projectScopeIds[type].has(String(row.id)))
      : rows;
  $: listOfProcesses = selectedDepartment
    ? populateList(
        inProjectScope("Processes", processRows).filter((p) => {
          const dept = deptRows.find((d) => d.id === selectedDepartment);
          return dept?.processes?.some((dp) => dp.id === p.id);
        }),
      )
    : populateList(inProjectScope("Processes", processRows));
  $: listOfSubprocesses = selectedProcess
    ? populateList(
        inProjectScope("Subprocesses", subprocessesRows).filter(
          (sp) => sp.process_id === selectedProcess,
        ),
      )
    : populateList(inProjectScope("Subprocesses", subprocessesRows));
  $: listOfTasks = selectedSubprocess
    ? populateList(
        inProjectScope("Tasks", taskRows).filter(
          (t) => t.subprocess_id === selectedSubprocess,
        ),
      )
    : populateList(inProjectScope("Tasks", taskRows));
  $: listOfSubtasks = populateList(
    inProjectScope("Subtasks", subtaskRows).filter((st) => {
      const task = taskRows.find((t) => t.id === st.task_id);
      if (selectedTask) return st.task_id === selectedTask;
      if (selectedSubprocess) return task?.subprocess_id === selectedSubprocess;
      if (selectedProcess) {
        const sp = subprocessesRows.find((s) => s.id === task?.subprocess_id);
        return sp?.process_id === selectedProcess;
      }
      if (selectedDepartment) {
        const sp = subprocessesRows.find((s) => s.id === task?.subprocess_id);
        const dept = deptRows.find((d) => d.id === selectedDepartment);
        return dept?.processes?.some((p) => p.id === sp?.process_id);
      }
      return true;
    }),
  );

  $: listOfOrgBusinessUnits = populateList(orgBusinessUnits);
  $: listOfOrgDepartments = populateList(
    selectedOrgBusinessUnit
      ? orgDepartments.filter(
          (department) =>
            getOrgBusinessUnitId(department) === selectedOrgBusinessUnit,
        )
      : orgDepartments,
  );
  $: listOfOrgAreas = populateList(
    selectedOrgDepartment
      ? orgAreas.filter(
          (area) => getOrgDepartmentId(area) === selectedOrgDepartment,
        )
      : orgAreas,
  );
  $: if (
    selectedOrgDepartment &&
    !listOfOrgDepartments.some((option) => option.key === selectedOrgDepartment)
  ) {
    selectedOrgDepartment = "";
  }
  $: if (
    selectedOrgArea &&
    !listOfOrgAreas.some((option) => option.key === selectedOrgArea)
  ) {
    selectedOrgArea = "";
  }
  $: orgFilteredFrameworkRows = orgFrameworkRows.filter(
    (row) =>
      (!selectedOrgBusinessUnit ||
        row.businessUnit?.id === selectedOrgBusinessUnit) &&
      (!selectedOrgDepartment ||
        row.department?.id === selectedOrgDepartment) &&
      (!selectedOrgArea || row.area?.id === selectedOrgArea),
  );
  $: {
    orgFilteredFrameworkRows;
    collapsedOrgBusinessUnits;
    visibleOrgFrameworkRows = getVisibleOrgFrameworkRows(
      orgFilteredFrameworkRows,
    );
  }
  $: orgFrameworkPageCount = Math.max(
    1,
    Math.ceil(visibleOrgFrameworkRows.length / orgFrameworkPageSize),
  );
  $: if (orgFrameworkPage > orgFrameworkPageCount) {
    orgFrameworkPage = orgFrameworkPageCount;
  }
  $: orgFrameworkPagedRows = visibleOrgFrameworkRows.slice(
    (orgFrameworkPage - 1) * orgFrameworkPageSize,
    orgFrameworkPage * orgFrameworkPageSize,
  );

  // Cascade reset: clear child selections when parent changes
  $: if (selectedDepartment !== undefined) {
    // Check if current process selection is still valid
    if (
      selectedProcess &&
      !listOfProcesses.some((p) => p.key === selectedProcess)
    ) {
      selectedProcess = "";
    }
  }
  $: if (selectedProcess !== undefined) {
    if (
      selectedSubprocess &&
      !listOfSubprocesses.some((sp) => sp.key === selectedSubprocess)
    ) {
      selectedSubprocess = "";
    }
  }
  $: if (selectedSubprocess !== undefined) {
    if (selectedTask && !listOfTasks.some((t) => t.key === selectedTask)) {
      selectedTask = "";
    }
  }
  $: if (selectedTask !== undefined) {
    if (
      selectedSubtask &&
      !listOfSubtasks.some((st) => st.key === selectedSubtask)
    ) {
      selectedSubtask = "";
    }
  }

  $: types = [
    { type: "Departments", headers: deptHeaders, rows: deptRows },
    { type: "Processes", headers: processHeaders, rows: processRows },
    { type: "Objects", headers: objectHeaders, rows: objectRows },
    {
      type: "Subprocesses",
      headers: subprocessesHeaders,
      rows: subprocessesRows,
    },
    { type: "Tasks", headers: taskHeaders, rows: taskRows },
    { type: "Subtasks", headers: subtaskHeaders, rows: subtaskRows },
  ];

  function filterRowsBySelection(rows, type) {
    if (type === "Departments") {
      let filtered = rows;
      if (selectedDepartment) {
        filtered = filtered.filter((r) => r.id === selectedDepartment);
      } else if (selectedProcess) {
        // Filter departments that contain the selected process
        filtered = filtered.filter((r) =>
          r.processes?.some((p) => p.id === selectedProcess),
        );
      } else if (selectedSubprocess) {
        const sp = subprocessesRows.find((s) => s.id === selectedSubprocess);
        const processId = sp?.process_id;
        filtered = filtered.filter((r) =>
          r.processes?.some((p) => p.id === processId),
        );
      } else if (selectedSubtask) {
        const subtask = subtaskRows.find((st) => st.id === selectedSubtask);
        const task = taskRows.find((t) => t.id === subtask?.task_id);
        const sp = subprocessesRows.find((s) => s.id === task?.subprocess_id);
        const processId = sp?.process_id;
        filtered = filtered.filter((r) =>
          r.processes?.some((p) => p.id === processId),
        );
      } else if (selectedTask) {
        const task = taskRows.find((t) => t.id === selectedTask);
        const sp = subprocessesRows.find((s) => s.id === task?.subprocess_id);
        const processId = sp?.process_id;
        filtered = filtered.filter((r) =>
          r.processes?.some((p) => p.id === processId),
        );
      }
      return filtered;
    } else if (type === "Processes") {
      let filtered = rows;
      if (selectedProcess) {
        filtered = filtered.filter((r) => r.id === selectedProcess);
      } else if (selectedSubprocess) {
        const sp = subprocessesRows.find((s) => s.id === selectedSubprocess);
        filtered = filtered.filter((r) => r.id === sp?.process_id);
      } else if (selectedSubtask) {
        const subtask = subtaskRows.find((st) => st.id === selectedSubtask);
        const task = taskRows.find((t) => t.id === subtask?.task_id);
        const sp = subprocessesRows.find((s) => s.id === task?.subprocess_id);
        filtered = filtered.filter((r) => r.id === sp?.process_id);
      } else if (selectedTask) {
        const task = taskRows.find((t) => t.id === selectedTask);
        const sp = subprocessesRows.find((s) => s.id === task?.subprocess_id);
        filtered = filtered.filter((r) => r.id === sp?.process_id);
      } else if (selectedDepartment) {
        const dept = deptRows.find((d) => d.id === selectedDepartment);
        const processIds = dept?.processes?.map((p) => p.id) || [];
        filtered = filtered.filter((r) => processIds.includes(r.id));
      }
      return filtered;
    } else if (type === "Subprocesses") {
      let filtered = rows;
      if (selectedSubprocess) {
        filtered = filtered.filter((r) => r.id === selectedSubprocess);
      } else if (selectedSubtask) {
        const subtask = subtaskRows.find((st) => st.id === selectedSubtask);
        const task = taskRows.find((t) => t.id === subtask?.task_id);
        filtered = filtered.filter((r) => r.id === task?.subprocess_id);
      } else if (selectedTask) {
        const task = taskRows.find((t) => t.id === selectedTask);
        filtered = filtered.filter((r) => r.id === task?.subprocess_id);
      } else if (selectedProcess) {
        filtered = filtered.filter((r) => r.process_id === selectedProcess);
      } else if (selectedDepartment) {
        const dept = deptRows.find((d) => d.id === selectedDepartment);
        const processIds = dept?.processes?.map((p) => p.id) || [];
        filtered = filtered.filter((r) => processIds.includes(r.process_id));
      }
      return filtered;
    } else if (type === "Tasks") {
      let filtered = rows;
      if (selectedSubtask) {
        const subtask = subtaskRows.find((st) => st.id === selectedSubtask);
        filtered = filtered.filter((r) => r.id === subtask?.task_id);
      } else if (selectedTask) {
        filtered = filtered.filter((r) => r.id === selectedTask);
      } else if (selectedSubprocess) {
        filtered = filtered.filter(
          (r) => r.subprocess_id === selectedSubprocess,
        );
      } else if (selectedProcess) {
        const spIds = subprocessesRows
          .filter((sp) => sp.process_id === selectedProcess)
          .map((sp) => sp.id);
        filtered = filtered.filter((r) => spIds.includes(r.subprocess_id));
      } else if (selectedDepartment) {
        const dept = deptRows.find((d) => d.id === selectedDepartment);
        const processIds = dept?.processes?.map((p) => p.id) || [];
        const spIds = subprocessesRows
          .filter((sp) => processIds.includes(sp.process_id))
          .map((sp) => sp.id);
        filtered = filtered.filter((r) => spIds.includes(r.subprocess_id));
      }
      return filtered;
    } else if (type === "Subtasks") {
      let filtered = rows;
      if (selectedSubtask) {
        filtered = filtered.filter((r) => r.id === selectedSubtask);
      } else if (selectedTask) {
        filtered = filtered.filter((r) => r.task_id === selectedTask);
      } else if (selectedSubprocess) {
        const tIds = taskRows
          .filter((t) => t.subprocess_id === selectedSubprocess)
          .map((t) => t.id);
        filtered = filtered.filter((r) => tIds.includes(r.task_id));
      } else if (selectedProcess) {
        const spIds = subprocessesRows
          .filter((sp) => sp.process_id === selectedProcess)
          .map((sp) => sp.id);
        const tIds = taskRows
          .filter((t) => spIds.includes(t.subprocess_id))
          .map((t) => t.id);
        filtered = filtered.filter((r) => tIds.includes(r.task_id));
      } else if (selectedDepartment) {
        const dept = deptRows.find((d) => d.id === selectedDepartment);
        const processIds = dept?.processes?.map((p) => p.id) || [];
        const spIds = subprocessesRows
          .filter((sp) => processIds.includes(sp.process_id))
          .map((sp) => sp.id);
        const tIds = taskRows
          .filter((t) => spIds.includes(t.subprocess_id))
          .map((t) => t.id);
        filtered = filtered.filter((r) => tIds.includes(r.task_id));
      }
      return filtered;
    }
    return rows;
  }

  $: pagedTypes = (() => {
    idproject;
    processRows;
    subprocessesRows;
    selectedDepartment;
    selectedProcess;
    selectedSubprocess;
    selectedTask;
    selectedSubtask;
    return types.map((t) => {
      const selectionFiltered = filterRowsBySelection(t.rows, t.type);
      const term = searchTerms[t.type];
      const filtered = selectionFiltered.filter((row) =>
        filterRow(row, term, t.headers),
      );
      const p = paginations[t.type];
      const start = (p.page - 1) * p.pageSize;
      return {
        ...t,
        missing: getFrameworkMissingEntries(t.type, t.rows),
        filteredCount: filtered.length,
        pagedRows: filtered.slice(start, start + p.pageSize),
      };
    });
  })();

  $: frameworkHasBlockingIssues =
    pagedTypes.some((type) => type.missing.length > 0) ||
    departmentAssignmentMissing;

  $: if (isEditMode && selectedType) {
    previousToSelect = getOptionsForType(selectedType);
    nextToSelect = getNextOptionsForType(selectedType);
    getSelected(selectedType);
  } else if (!isEditMode && selectedAddType) {
    previousToSelect = getOptionsForType(selectedAddType);
    nextToSelect = getNextOptionsForType(selectedAddType);

    if (!isSubordinateMode) {
      selectedPrevIds = [];
      selectedPrevId = "";
      selectedNextIds = [];
    }
  }

  $: {
    idproject;
    selectedPrevId;
    selectedObject;
    deptRows;
    processRows;
    objectRows;
    subprocessesRows;
    taskRows;
    subtaskRows;
    frameworkNameValidation = {
      duplicate: getFrameworkModalNameDuplicate(
        isEditMode ? selectedType : selectedAddType,
        newName,
      ),
    };
  }

  $: isPrimaryButtonNotAllowed =
    !newName.trim() ||
    frameworkModalSaving ||
    frameworkNameValidation.duplicate ||
    (isEditMode &&
      (!selectedType ||
        (selectedType === "Processes" && !selectedPrevIds.length) ||
        ((selectedType === "Subprocesses" ||
          selectedType === "Tasks" ||
          selectedType === "Subtasks") &&
          !selectedPrevId))) ||
    (!isEditMode &&
      (!selectedAddType ||
        (selectedAddType === "Processes" &&
          !(projectScopeActive
            ? projectDepartmentRows.length
            : selectedPrevIds.length)) ||
        ((selectedAddType === "Subprocesses" ||
          selectedAddType === "Tasks" ||
          selectedAddType === "Subtasks") &&
          !selectedPrevId)));

  $: companyMissingPathLevels = getCompanyMissingPathLevels(
    companyMissingPathRow,
    companyMissingPathTarget,
  );
  $: companyMissingPathCanSave =
    companyMissingPathLevels.length > 0 &&
    companyMissingPathLevels.every((level) =>
      companyMissingPathNames[level]?.trim(),
    );
  // Fehlt nur eine Ebene, wie ein normales Anlegen beschriften statt als Pfad-Vervollstaendigung
  $: companyMissingPathSingleLabel =
    companyMissingPathLevels.length === 1
      ? getCompanyPathLabel(companyMissingPathLevels[0])
      : "";
  $: companyMissingPathSubtitle = companyMissingPathSingleLabel
    ? ""
    : companyMissingPathLevels.length
      ? `The following elements will be selected or created and linked: ${companyMissingPathLevels.map(getCompanyPathLabel).join(" → ")}.`
      : "Known hierarchy elements are kept.";

  function filterRow(row, term, headers) {
    if (!term) return true;
    const lower = term.toLowerCase();
    const searchableKeys = headers
      .filter((h) => !["edit", "delete", "actions"].includes(h.key))
      .map((h) => h.key);
    return searchableKeys.some((key) => {
      const val = row[key];
      if (val === undefined || val === null) return false;
      if (typeof val === "string") return val.toLowerCase().includes(lower);
      if (Array.isArray(val))
        return val.some((item) =>
          item && typeof item === "object" && item.name
            ? item.name.toLowerCase().includes(lower)
            : false,
        );
      return false;
    });
  }

  function populateList(rows) {
    return rows.map((item) => ({
      key: item.id,
      value: item.name,
    }));
  }

  function findItemByName(rows, name, parentKey = "", parentId = "") {
    const normalizedName = name.trim().toLocaleLowerCase();
    if (!normalizedName) return null;
    return rows.find(
      (item) =>
        item.name?.trim().toLocaleLowerCase() === normalizedName &&
        (!parentKey || item[parentKey] === parentId),
    );
  }

  function findOptionKeyByValue(options, value) {
    const normalizedValue = value.trim().toLocaleLowerCase();
    if (!normalizedValue) return "";
    return (
      options.find(
        (option) =>
          option.value?.trim().toLocaleLowerCase() === normalizedValue,
      )?.key || ""
    );
  }

  function assignGroupColors(rows, getGroupKey) {
    const colors = [
      "#a9d6e5",
      "#b7e4c7",
      "#d9c2f0",
      "#f7d6a4",
      "#f5c2c7",
      "#c9e4de",
      "#d3d6f8",
      "#e8d4b6",
    ];
    const groupColorIndexes = new Map();

    return rows.map((row) => {
      const groupKey = getGroupKey(row);
      if (!groupColorIndexes.has(groupKey)) {
        groupColorIndexes.set(groupKey, groupColorIndexes.size);
      }

      return {
        ...row,
        groupColor: colors[groupColorIndexes.get(groupKey) % colors.length],
      };
    });
  }

  function getOrgBusinessUnitId(department) {
    return String(
      department?.business_unit?.id ||
        department?.business_unit_id ||
        department?.business_unit ||
        "",
    );
  }

  function getOrgDepartmentId(area) {
    return String(
      area?.department?.id || area?.department_id || area?.department || "",
    );
  }

  function getNameByType(type) {
    const option = typeOptions.find((opt) => opt.id === type);
    return option ? option.text : "";
  }

  function getFrameworkInfo(type) {
    const descriptions = {
      Departments:
        "Create and manage the departments included in this project.",
      Processes:
        "Define the business processes that belong to the selected departments.",
      Objects:
        "Add the business objects that are relevant for the project processes.",
      Subprocesses:
        "Break processes down into the subprocesses used in daily operations.",
      Tasks: "Define the individual tasks that make up each subprocess.",
      Subtasks: "Add the detailed steps that belong to each task.",
    };
    return (
      descriptions[type] ||
      "Configure the framework elements used in this project."
    );
  }

  function getMissingChildEntries(type, rows = []) {
    if (type === "Departments") {
      return rows.filter(
        (row) =>
          !(row.processes || []).length &&
          !processRows.some((process) =>
            (process.departments || []).some(
              (department) => String(department.id) === String(row.id),
            ),
          ),
      );
    }
    if (type === "Processes") {
      return rows.filter(
        (row) =>
          !subprocessesRows.some(
            (subprocess) => String(subprocess.process_id) === String(row.id),
          ),
      );
    }
    if (type === "Subprocesses") {
      return rows.filter(
        (row) =>
          !taskRows.some(
            (task) => String(task.subprocess_id) === String(row.id),
          ),
      );
    }
    if (type === "Tasks") {
      return rows.filter(
        (row) =>
          !subtaskRows.some(
            (subtask) => String(subtask.task_id) === String(row.id),
          ),
      );
    }
    return [];
  }

  function getMissingLinkedEntries(type, rows = []) {
    const hasId = (items, id) =>
      Boolean(id) &&
      (items || []).some((item) => String(item.id) === String(id));

    if (type === "Departments") {
      if (idproject) {
        return rows.filter(
          (row) =>
            !(row.projects || []).some(
              (project) => String(project.id) === String(idproject),
            ),
        );
      }
      return rows.filter((row) => !(row.projects || []).length);
    }
    if (type === "Processes") {
      return rows.filter((row) => !(row.departments || []).length);
    }
    if (type === "Objects") {
      return rows.filter((row) => !(row.processes || []).length);
    }
    if (type === "Subprocesses") {
      return rows.filter((row) => !hasId(processRows, row.process_id));
    }
    if (type === "Tasks") {
      return rows.filter((row) => !hasId(subprocessesRows, row.subprocess_id));
    }
    return [];
  }

  function getFrameworkMissingEntries(type, rows = []) {
    const missing = [
      ...getMissingLinkedEntries(type, rows),
      ...getMissingChildEntries(type, rows),
    ];
    return Array.from(new Map(missing.map((row) => [row.id, row])).values());
  }

  function getFrameworkNoticeMessage(type, rows) {
    const missingEntries = getFrameworkMissingEntries(type, rows);
    if (!missingEntries.length) return getFrameworkInfo(type);

    const linkedEntries = getMissingLinkedEntries(type, rows);
    const childEntries = getMissingChildEntries(type, rows);
    const parentLabels = {
      Departments: "project",
      Processes: "department",
      Objects: "process",
      Subprocesses: "process",
      Tasks: "subprocess",
    };
    const childLabels = {
      Departments: "process",
      Processes: "subprocess",
      Subprocesses: "task",
      Tasks: "subtask",
    };
    const messages = [];

    if (linkedEntries.length && parentLabels[type]) {
      messages.push(
        `The following entries have no linked ${parentLabels[type]}: ${linkedEntries
          .map((entry) => entry.name)
          .join(", ")}`,
      );
    }
    if (childEntries.length) {
      messages.push(
        `The following entries have no ${childLabels[type]} underneath: ${childEntries
          .map((entry) => entry.name)
          .join(", ")}`,
      );
    }
    return messages.join(" ");
  }

  function getNextOptionsForType(type) {
    if (type === "Departments" || type === "Department") {
      return processRows.map(({ id, name }) => ({ id, text: name }));
    } else if (type === "Processes" || type === "Process") {
      return objectRows.map(({ id, name }) => ({ id, text: name }));
    } else if (type === "Objects" || type === "Object") {
      return [];
    } else if (type === "Subprocesses" || type === "Subprocess") {
      return taskRows.map(({ id, name }) => ({ id, text: name }));
    } else if (type === "Tasks" || type === "Task") {
      return [];
    } else if (type === "Subtasks" || type === "Subtask") {
      return [];
    }
    return [];
  }

  function getOptionsForType(type) {
    if (type === "Departments" || type === "Department") {
      return projects.map(({ id, name }) => ({ id, text: name }));
    } else if (type === "Processes" || type === "Process") {
      return deptRows.map(({ id, name }) => ({ id, text: name }));
    } else if (type === "Objects" || type === "Object") {
      return inProjectScope("Processes", processRows).map(({ id, name }) => ({
        id,
        text: name,
      }));
    } else if (type === "Subprocesses" || type === "Subprocess") {
      // Im Projekt nur Eltern aus den Projekt-Departments anbieten (wie die Filterlisten)
      return inProjectScope("Processes", processRows).map(({ id, name }) => ({
        id,
        text: name,
      }));
    } else if (type === "Tasks" || type === "Task") {
      return inProjectScope("Subprocesses", subprocessesRows).map(
        ({ id, name, process_id }) => ({
          id,
          text: `${name} — ${processRows.find((process) => process.id === process_id)?.name || "No process"}`,
        }),
      );
    } else if (type === "Subtasks" || type === "Subtask") {
      return inProjectScope("Tasks", taskRows).map(
        ({ id, name, subprocess_id }) => {
          const subprocess = subprocessesRows.find(
            (item) => item.id === subprocess_id,
          );
          const process = processRows.find(
            (item) => item.id === subprocess?.process_id,
          );
          return {
            id,
            text: `${name} — ${subprocess?.name || "No subprocess"}${process ? ` — ${process.name}` : ""}`,
          };
        },
      );
    }
    return [];
  }

  function filterFrameworkParentOption(item, value) {
    return (
      !value ||
      item.text.toLocaleLowerCase().includes(value.toLocaleLowerCase())
    );
  }

  function clearFrameworkParentOnSearch(event) {
    if (event.key.length === 1 || ["Backspace", "Delete"].includes(event.key)) {
      selectedPrevId = "";
    }
  }

  function getSelected(selectedType) {
    if (selectedType === "Departments") {
      selectedPrevIds = selectedObject.projects
        ? selectedObject.projects.map((p) => p.id)
        : [];
      selectedNextIds = selectedObject.processes
        ? selectedObject.processes.map((proc) => proc.id)
        : [];
    } else if (selectedType === "Processes") {
      selectedPrevIds = selectedObject.departments
        ? selectedObject.departments.map((d) => d.id)
        : [];
      selectedNextIds = selectedObject.objects
        ? selectedObject.objects.map((obj) => obj.id)
        : [];
    } else if (selectedType === "Objects") {
      selectedPrevIds = selectedObject.processes
        ? selectedObject.processes.map((proc) => proc.id)
        : [];
      selectedNextIds = [];
    } else if (selectedType === "Subprocesses") {
      selectedPrevIds = selectedObject.processes
        ? selectedObject.processes.map((proc) => proc.id)
        : [];
      selectedNextIds = selectedObject.tasks
        ? selectedObject.tasks.map((task) => task.id)
        : [];
    } else if (selectedType === "Tasks") {
      selectedPrevIds = selectedObject.subProcesses
        ? selectedObject.subProcesses.map((sp) => sp.id)
        : [];
      selectedNextIds = [];
    } else if (selectedType === "Subtasks") {
      selectedPrevIds = selectedObject.tasks
        ? selectedObject.tasks.map((task) => task.id)
        : [];
      selectedNextIds = [];
    }
  }

  function getFrameworkDeleteImpact(type, object) {
    if (!object) return [];
    const subprocessIds = new Set(
      type === "Processes"
        ? subprocessesRows
            .filter((item) => String(item.process_id) === String(object.id))
            .map((item) => String(item.id))
        : type === "Subprocesses"
          ? [String(object.id)]
          : [],
    );
    const impactedSubprocesses = subprocessesRows.filter((item) =>
      subprocessIds.has(String(item.id)),
    );
    const taskIds = new Set(
      type === "Tasks"
        ? [String(object.id)]
        : taskRows
            .filter((item) => subprocessIds.has(String(item.subprocess_id)))
            .map((item) => String(item.id)),
    );
    const impactedTasks = taskRows.filter((item) =>
      taskIds.has(String(item.id)),
    );
    const impactedSubtasks = subtaskRows.filter((item) =>
      taskIds.has(String(item.task_id)),
    );
    return [
      { label: "subprocesses", items: impactedSubprocesses },
      { label: "tasks", items: impactedTasks },
      { label: "subtasks", items: impactedSubtasks },
    ].filter((impact) => impact.items.length);
  }

  function isOrgManagedDepartment(type, object) {
    if (type !== "Departments" || !object) return false;
    const name = object.name?.trim().toLocaleLowerCase();
    return orgDepartments.some(
      (department) =>
        String(department.id) === String(object.id) ||
        department.name?.trim().toLocaleLowerCase() === name,
    );
  }

  async function getDepartments() {
    const response = await fetchWithSession("/api/damprojects/departments");
    const data = await response.json();
    deptRows = data;
    mergeOrgDepartmentsIntoProcessFramework();
  }

  async function getProcesses() {
    const response = await fetchWithSession("/api/damprojects/processes");
    const data = await response.json();
    processRows = data;
    impactSummary.processes = processRows.length;
  }

  async function getObjects() {
    const response = await fetchWithSession("/api/damprojects/dam-objects");
    const data = await response.json();
    objectRows = data;
  }

  async function getSubprocesses() {
    const response = await fetchWithSession("/api/damprojects/subprocesses");
    const data = await response.json();
    subprocessesRows = data;
    subprocessesRows.forEach((subprocess) => {
      subprocess.process = processRows.find(
        (proc) => proc.id === subprocess.process_id,
      )?.name;
    });
    impactSummary.subprocesses = subprocessesRows.length;
  }

  async function getTasks() {
    const response = await fetchWithSession("/api/damprojects/tasks");
    const data = await response.json();
    taskRows = data;
    taskRows.forEach((task) => {
      task.subprocess = subprocessesRows.find(
        (sp) => sp.id === task.subprocess_id,
      )?.name;
    });
    impactSummary.tasks = taskRows.length;
  }

  async function getSubTasks() {
    const response = await fetchWithSession("/api/damprojects/subtasks");
    const data = await response.json();
    subtaskRows = data;
    subtaskRows.forEach((subtask) => {
      subtask.task = taskRows.find((t) => t.id === subtask.task_id)?.name;
    });
    impactSummary.subtasks = subtaskRows.length;
  }

  function getFrameworkEndpoint(type) {
    let typeName = type.toLowerCase();
    if (type === "Objects") typeName = "dam-objects";
    if (type === "Subprocesses") typeName = "subprocesses";
    if (type === "Subtasks") typeName = "subtasks";
    return typeName;
  }

  async function deleteSelected() {
    if (!selectedObject || !selectedType || frameworkDeleteSaving) return;
    if (isOrgManagedDepartment(selectedType, selectedObject)) {
      frameworkDeleteError =
        "This department must be deleted under Company Settings → Organization Framework.";
      return;
    }
    frameworkDeleteSaving = true;
    frameworkDeleteError = "";
    try {
      const response = await fetchWithSession(
        `/api/damprojects/${getFrameworkEndpoint(selectedType)}/${selectedObject.id}`,
        { method: "DELETE" },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok === false) {
        throw new Error(
          data?.error || data?.message || "Could not delete the element.",
        );
      }
      await refreshTables();
      openDangerModal = false;
    } catch (error) {
      frameworkDeleteError = error?.message || "Could not delete the element.";
    } finally {
      frameworkDeleteSaving = false;
    }
  }

  async function createElement() {
    if (isPrimaryButtonNotAllowed) return;
    let body = { name: newName.trim() };
    if (selectedAddType === "Departments") {
      body.projects = selectedPrevIds;
      body.processes = selectedNextIds;
    } else if (selectedAddType === "Processes") {
      // Im Projekt-Framework immer das Projekt-Department (im Modal nur als Info angezeigt)
      body.departments = projectScopeActive
        ? projectDepartmentRows.map((department) => department.id)
        : selectedPrevIds;
      body.objects = selectedNextIds;
    } else if (selectedAddType === "Objects") {
      body.processes = selectedPrevIds;
    } else if (selectedAddType === "Subprocesses") {
      body.process = selectedPrevId;
    } else if (selectedAddType === "Tasks") {
      body.subprocess = selectedPrevId;
    } else if (selectedAddType === "Subtasks") {
      body.task = selectedPrevId;
    }

    frameworkModalSaving = true;
    frameworkModalError = "";
    try {
      const response = await fetchWithSession(
        `/api/damprojects/${getFrameworkEndpoint(selectedAddType)}`,
        {
          method: "POST",
          body: JSON.stringify(body),
          headers: { "Content-Type": "application/json" },
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok === false) {
        throw new Error(
          data?.error || data?.message || "Could not create the element.",
        );
      }
      await refreshTables();
      openModalAdd = false;
    } catch (error) {
      frameworkModalError = error?.message || "Could not create the element.";
    } finally {
      frameworkModalSaving = false;
    }
  }

  async function updateElement() {
    if (isPrimaryButtonNotAllowed || !selectedObject) return;

    let body = { name: newName.trim() };
    if (selectedType === "Departments") {
      body.projects = selectedPrevIds;
      body.processes = selectedNextIds;
    } else if (selectedType === "Processes") {
      body.departments = selectedPrevIds;
      body.objects = selectedNextIds;
    } else if (selectedType === "Objects") {
      body.processes = selectedPrevIds;
    } else if (selectedType === "Subprocesses") {
      body.process = selectedPrevId;
    } else if (selectedType === "Tasks") {
      body.subprocess = selectedPrevId;
    } else if (selectedType === "Subtasks") {
      body.task = selectedPrevId;
    }

    frameworkModalSaving = true;
    frameworkModalError = "";
    try {
      const response = await fetchWithSession(
        `/api/damprojects/${getFrameworkEndpoint(selectedType)}/${selectedObject.id}`,
        {
          method: "PATCH",
          body: JSON.stringify(body),
          headers: { "Content-Type": "application/json" },
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok === false) {
        throw new Error(
          data?.error || data?.message || "Could not update the element.",
        );
      }
      await refreshTables();
      openModalAdd = false;
    } catch (error) {
      frameworkModalError = error?.message || "Could not update the element.";
    } finally {
      frameworkModalSaving = false;
    }
  }

  function openAddModal() {
    isEditMode = false;
    isSubordinateMode = false;
    selectedType = "";
    selectedAddType = "";
    selectedObject = null;
    newName = "";
    selectedPrevIds = [];
    selectedPrevId = "";
    selectedNextIds = [];
    frameworkModalError = "";
    openModalAdd = true;
  }

  function openSubordinateModal(parentType, row) {
    isEditMode = false;
    isSubordinateMode = true;
    selectedType = "";
    selectedObject = null;
    newName = "";
    selectedPrevIds = [];
    selectedPrevId = "";
    selectedNextIds = [];
    frameworkModalError = "";

    if (parentType === "Departments") {
      selectedAddType = "Processes";
      selectedPrevIds = [row.id];
    } else if (parentType === "Processes") {
      selectedAddType = "Subprocesses";
      selectedPrevId = row.id;
    } else if (parentType === "Subprocesses") {
      selectedAddType = "Tasks";
      selectedPrevId = row.id;
    } else if (parentType === "Tasks") {
      selectedAddType = "Subtasks";
      selectedPrevId = row.id;
    }

    openModalAdd = true;
  }

  function openEditModal(type, object) {
    isEditMode = true;
    selectedType = type;
    selectedAddType = "";
    selectedObject = object;
    newName = object.name || "";
    frameworkModalError = "";
    if (type === "Processes") {
      selectedPrevIds = (object.departments || []).map((dept) => dept.id);
      selectedNextIds = (object.objects || []).map((obj) => obj.id);
    } else if (type === "Objects" && object.processes) {
      selectedPrevIds = object.processes.map((proc) => proc.id);
    } else if (type === "Departments") {
      selectedPrevIds = (object.projects || []).map((project) => project.id);
      selectedNextIds = (object.processes || []).map((process) => process.id);
    } else if (type === "Subprocesses") {
      selectedPrevId = object.process_id;
    } else if (type === "Tasks") {
      selectedPrevId = object.subprocess_id;
    } else if (type === "Subtasks") {
      selectedPrevId = object.task_id;
    }
    openModalAdd = true;
  }

  async function refreshTables() {
    await getDepartments();
    await getProcesses();
    await getObjects();
    await getSubprocesses();
    await getTasks();
    await getSubTasks();
  }

  function setSelectedValues(selectedIds, items) {
    const values = Object.entries(items)
      .filter(([key, value]) => selectedIds.includes(value.id))
      .map(([key, value]) => value.text);
    return '"' + values.join('" , "') + '"';
  }

  function cleanModal() {
    selectedType = "";
    selectedAddType = "";
    isSubordinateMode = false;
    selectedPrevIds = [];
    selectedPrevId = "";
    selectedNextIds = [];
    newName = "";
    frameworkModalError = "";
  }

  function handleOpenImport() {
    console.log("Opening import modal...");
    if (
      processRows.length ||
      subprocessesRows.length ||
      taskRows.length ||
      subtaskRows.length
    ) {
      openExistingFrameworkModal = true;
    } else {
      openImportModal = true;
    }
  }

  function openFrameworkAssistant() {
    navigate(ROUTES.frameworkAssistant);
  }

  function handleContinue(event) {
    console.log(
      "Continuing with framework import...",
      event.detail.replaceFramework,
    );
    replaceFramework = event.detail.replaceFramework;
    openExistingFrameworkModal = false;
    openImportModal = true;
  }

  async function handleImportSubmit(event) {
    const { rows } = event.detail;
    console.log("Rows:", rows);
    console.log("handleImportSubmit", { replaceFramework });
    //  fetchWithSession("/api/damprojects/multiple-project-presets", {
    //     method: "POST",
    //     body: JSON.stringify({ rows, replace: replaceFramework }),
    //   })
    //     .then((response) => response.json())
    //     .then((data) => {
    //       saveSuccessful = data.ok;
    //       toastMsg = data.message || data.error;
    //       if (saveSuccessful) refreshTables();
    //     });
    if (replaceFramework) {
      console.log("Replacing existing framework with imported data...");
    } else {
      console.log("Merging imported data with existing framework...");
      const rowsByDepartment = new Map();
      rows.forEach((row) => {
        const department = row.department_l0?.trim() || "";
        const key = department.toLocaleLowerCase();
        if (!rowsByDepartment.has(key)) {
          rowsByDepartment.set(key, { department, rows: [] });
        }
        rowsByDepartment.get(key).rows.push({
          process_l1: row.process_l1,
          subprocess_l2: row.subprocess_l2,
          task_l3: row.task_l3,
          subtask_l4: row.subtask_l4,
        });
      });

      for (const group of rowsByDepartment.values()) {
        const response = await fetchWithSession(
          "/api/damprojects/multiple-project-presets",
          {
            method: "POST",
            body: JSON.stringify({
              rows: group.rows,
              departments: group.department ? [group.department] : [],
            }),
          },
        );
        const data = await response.json();
        if (!response.ok || data.ok === false || data.success === false) {
          throw new Error(data.message || data.error || "Import failed.");
        }
      }
      await refreshTables();
    }
    openImportModal = false;
  }

  function resetFilters() {
    selectedDepartment = "";
    selectedProcess = "";
    selectedSubprocess = "";
    selectedTask = "";
    selectedSubtask = "";
    companyFrameworkPage = 1;
    companyFrameworkSearch = "";
    companyFrameworkCompleteness = "";
    filterKey++;
  }

  function resetOrgFilters() {
    selectedOrgBusinessUnit = "";
    selectedOrgDepartment = "";
    selectedOrgArea = "";
    orgFrameworkPage = 1;
    orgFilterKey++;
  }

  $: {
    deptRows;
    processRows;
    subprocessesRows;
    taskRows;
    subtaskRows;
    projectDepartmentIds;
    // Im Projekt nur das Framework der Projekt-Departments zeigen
    // Zeilen nur mit Department (noch ohne Process) gibt es im Projekt nicht - dafuer steht der Hinweis darueber
    companyFrameworkRows = buildCompanyFrameworkRows().filter(
      (row) =>
        !projectScopeActive ||
        (row.process &&
          projectDepartmentIds.includes(String(row.department?.id))),
    );
  }
  $: filteredCompanyFrameworkRows = companyFrameworkRows.filter(
    (row) =>
      (!selectedDepartment || row.department?.id === selectedDepartment) &&
      (!selectedProcess || row.process?.id === selectedProcess) &&
      (!selectedSubprocess || row.subprocess?.id === selectedSubprocess) &&
      (!selectedTask || row.task?.id === selectedTask) &&
      (!selectedSubtask || row.subtask?.id === selectedSubtask) &&
      (companyFrameworkCompleteness !== "incomplete" ||
        [
          row.department,
          row.process,
          row.subprocess,
          row.task,
          row.subtask,
        ].some((entry) => !entry)),
  );
  $: searchedCompanyFrameworkRows = filteredCompanyFrameworkRows.filter((row) =>
    [row.department, row.process, row.subprocess, row.task, row.subtask].some(
      (entry) =>
        entry?.name
          ?.toLocaleLowerCase()
          .includes(companyFrameworkSearch.trim().toLocaleLowerCase()),
    ),
  );
  $: sortedCompanyFrameworkRows = sortCompanyFrameworkRows(
    searchedCompanyFrameworkRows,
    companyFrameworkSortKey,
    companyFrameworkSortDirection,
    frameworkGroupLevel,
  );
  $: {
    sortedCompanyFrameworkRows;
    companyFrameworkRows;
    collapsedCompanyDepartments;
    frameworkGroupLevel;
    visibleCompanyFrameworkRows = getVisibleCompanyFrameworkRows(
      sortedCompanyFrameworkRows,
      companyFrameworkCompleteness === "incomplete"
        ? sortedCompanyFrameworkRows
        : companyFrameworkRows,
      companyFrameworkCompleteness === "incomplete",
    );
  }
  $: {
    selectedDepartment;
    selectedProcess;
    selectedSubprocess;
    selectedTask;
    selectedSubtask;
    companyFrameworkSearch;
    companyFrameworkCompleteness;
    companyFrameworkSortKey;
    companyFrameworkSortDirection;
    companyFrameworkPageSize;
    companyFrameworkPage = 1;
  }
  $: projectScopeIds = {
    Departments: new Set(
      companyFrameworkRows.map((row) => String(row.department?.id)),
    ),
    Processes: new Set(
      companyFrameworkRows.map((row) => String(row.process?.id)),
    ),
    Subprocesses: new Set(
      companyFrameworkRows.map((row) => String(row.subprocess?.id)),
    ),
    Tasks: new Set(companyFrameworkRows.map((row) => String(row.task?.id))),
    Subtasks: new Set(
      companyFrameworkRows.map((row) => String(row.subtask?.id)),
    ),
  };
  $: scopedTypes = projectScopeActive
    ? types
        .filter((item) => item.type !== "Departments")
        .map((item) => ({
          ...item,
          rows: projectScopeIds[item.type]
            ? item.rows.filter((row) =>
                projectScopeIds[item.type].has(String(row.id)),
              )
            : item.rows,
        }))
    : types;
  $: companyFrameworkIssues = scopedTypes
    .map(({ type, rows }) => ({
      type,
      missingProjects:
        idproject && showLegacyProjectFramework && type === "Departments"
          ? getMissingLinkedEntries(type, rows).length
          : 0,
      missingParents:
        type === "Departments"
          ? 0
          : type === "Subtasks"
            ? rows.filter(
                (row) => !taskRows.some((task) => task.id === row.task_id),
              ).length
            : getMissingLinkedEntries(type, rows).length,
      missingChildren: getMissingChildEntries(type, rows).length,
    }))
    .filter(
      (issue) =>
        issue.missingProjects || issue.missingParents || issue.missingChildren,
    );
  $: companyQuickDepartmentId =
    findItemByName(deptRows, companyQuickDepartment)?.id || "";
  $: companyQuickProcesses = companyQuickDepartmentId
    ? populateList(
        processRows.filter((process) =>
          deptRows
            .find((department) => department.id === companyQuickDepartmentId)
            ?.processes?.some((item) => item.id === process.id),
        ),
      )
    : populateList(processRows);
  $: companyQuickProcessId = findOptionKeyByValue(
    companyQuickProcesses,
    companyQuickProcess,
  );
  $: companyQuickSubprocesses = companyQuickProcessId
    ? populateList(
        subprocessesRows.filter(
          (subprocess) => subprocess.process_id === companyQuickProcessId,
        ),
      )
    : populateList(subprocessesRows);
  $: companyQuickSubprocessId =
    findItemByName(
      subprocessesRows,
      companyQuickSubprocess,
      "process_id",
      companyQuickProcessId,
    )?.id || "";
  $: companyQuickTasks = companyQuickSubprocessId
    ? populateList(
        taskRows.filter(
          (task) => task.subprocess_id === companyQuickSubprocessId,
        ),
      )
    : populateList(taskRows);
  $: companyQuickTaskId =
    findItemByName(
      taskRows,
      companyQuickTask,
      "subprocess_id",
      companyQuickSubprocessId,
    )?.id || "";
  $: companyQuickSubtasks = companyQuickTaskId
    ? populateList(
        subtaskRows.filter((subtask) => subtask.task_id === companyQuickTaskId),
      )
    : populateList(subtaskRows);
  $: companyQuickAddAllowed =
    companyQuickDepartmentId &&
    companyQuickProcessId &&
    (companyQuickSubprocess || companyQuickTask || companyQuickSubtask);
  $: companyFrameworkPageCount = Math.max(
    1,
    Math.ceil(visibleCompanyFrameworkRows.length / companyFrameworkPageSize),
  );
  $: if (companyFrameworkPage > companyFrameworkPageCount) {
    companyFrameworkPage = companyFrameworkPageCount;
  }
  $: companyFrameworkPagedRows = visibleCompanyFrameworkRows.slice(
    (companyFrameworkPage - 1) * companyFrameworkPageSize,
    companyFrameworkPage * companyFrameworkPageSize,
  );

  function buildCompanyFrameworkRows() {
    const rows = [];
    const rowKeys = new Set();
    const departmentsByProcessId = new Map();
    const processById = new Map(
      processRows.map((process) => [process.id, process]),
    );
    const subprocessById = new Map(
      subprocessesRows.map((subprocess) => [subprocess.id, subprocess]),
    );
    const taskById = new Map(taskRows.map((task) => [task.id, task]));

    deptRows.forEach((department) => {
      department.processes?.forEach((process) => {
        const departments = departmentsByProcessId.get(process.id) || [];
        departments.push(department);
        departmentsByProcessId.set(process.id, departments);
      });
    });

    processRows.forEach((process) => {
      process.departments?.forEach((department) => {
        const storedDepartment = deptRows.find(
          (item) => item.id === department.id,
        );
        if (!storedDepartment) return;
        const departments = departmentsByProcessId.get(process.id) || [];
        if (!departments.some((item) => item.id === storedDepartment.id)) {
          departments.push(storedDepartment);
          departmentsByProcessId.set(process.id, departments);
        }
      });
    });

    function addRow(department, process, subprocess, task, subtask) {
      const key = [
        department?.id || "",
        process?.id || "",
        subprocess?.id || "",
        task?.id || "",
        subtask?.id || "",
      ].join(":");
      if (rowKeys.has(key)) return;
      rowKeys.add(key);
      rows.push({ id: key, department, process, subprocess, task, subtask });
    }

    function addWithDepartments(process, subprocess, task, subtask) {
      const departments = process
        ? departmentsByProcessId.get(process.id) || []
        : [];
      if (departments.length) {
        departments.forEach((department) =>
          addRow(department, process, subprocess, task, subtask),
        );
      } else {
        addRow(null, process, subprocess, task, subtask);
      }
    }

    subtaskRows.forEach((subtask) => {
      const task = taskById.get(subtask.task_id);
      const subprocess = task ? subprocessById.get(task.subprocess_id) : null;
      const process = subprocess
        ? processById.get(subprocess.process_id)
        : null;
      addWithDepartments(process, subprocess, task, subtask);
    });

    const tasksWithSubtasks = new Set(
      subtaskRows.map((subtask) => subtask.task_id),
    );
    taskRows
      .filter((task) => !tasksWithSubtasks.has(task.id))
      .forEach((task) => {
        const subprocess = subprocessById.get(task.subprocess_id);
        const process = subprocess
          ? processById.get(subprocess.process_id)
          : null;
        addWithDepartments(process, subprocess, task, null);
      });

    const subprocessesWithTasks = new Set(
      taskRows.map((task) => task.subprocess_id),
    );
    subprocessesRows
      .filter((subprocess) => !subprocessesWithTasks.has(subprocess.id))
      .forEach((subprocess) =>
        addWithDepartments(
          processById.get(subprocess.process_id),
          subprocess,
          null,
          null,
        ),
      );

    const processesWithSubprocesses = new Set(
      subprocessesRows.map((subprocess) => subprocess.process_id),
    );
    processRows
      .filter((process) => !processesWithSubprocesses.has(process.id))
      .forEach((process) => addWithDepartments(process, null, null, null));

    const departmentsWithProcesses = new Set(
      [...departmentsByProcessId.values()]
        .flat()
        .map((department) => department.id),
    );
    deptRows
      .filter((department) => !departmentsWithProcesses.has(department.id))
      .forEach((department) => addRow(department, null, null, null, null));

    const sortedRows = rows.sort((first, second) =>
      [
        first.department?.name,
        first.process?.name,
        first.subprocess?.name,
        first.task?.name,
        first.subtask?.name,
      ]
        .map((value) => value || "")
        .join("\u0000")
        .localeCompare(
          [
            second.department?.name,
            second.process?.name,
            second.subprocess?.name,
            second.task?.name,
            second.subtask?.name,
          ]
            .map((value) => value || "")
            .join("\u0000"),
        ),
    );

    return assignGroupColors(
      sortedRows,
      (row) => row.department?.id || "unassigned",
    );
  }

  function sortCompanyFrameworkRows(
    rows,
    key,
    direction,
    groupLevel = "department",
  ) {
    if (!key || direction === "none") return rows;
    const compare = (first, second) => {
      const a = first[key]?.name || "";
      const b = second[key]?.name || "";
      if (!a || !b) return a ? -1 : b ? 1 : 0;
      return (
        a.localeCompare(b, undefined, { numeric: true }) *
        (direction === "descending" ? -1 : 1)
      );
    };
    if (key === groupLevel) return [...rows].sort(compare);
    // Keep department groups contiguous so collapsing still hides the whole group.
    const groups = new Map();
    rows.forEach((row) => {
      const groupKey = row[groupLevel]?.id || "unassigned";
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey).push(row);
    });
    return [...groups.values()].flatMap((group) => group.sort(compare));
  }

  function getVisibleCompanyFrameworkRows(
    rows,
    allRows = rows,
    expandGroups = false,
  ) {
    const levels = ["department", "process", "subprocess", "task", "subtask"];
    const summaries = new Map();
    allRows.forEach((row) => {
      const key = row[frameworkGroupLevel]?.id || "unassigned";
      if (!summaries.has(key)) {
        summaries.set(key, {
          counts: Object.fromEntries(levels.map((level) => [level, new Set()])),
          missing: Object.fromEntries(
            levels.map((level) => [level, new Set()]),
          ),
        });
      }
      const summary = summaries.get(key);
      levels.forEach((level, index) => {
        if (row[level]) summary.counts[level].add(row[level].id);
        if (index === 0) return;
        const parent = levels[index - 1];
        if (row[parent] && !row[level])
          summary.missing[level].add(row[parent].id);
        if (!row[parent] && row[level])
          summary.missing[parent].add(row[level].id);
        if (!row[level]) {
          const closestParent = levels
            .slice(0, index)
            .reverse()
            .find((parentLevel) => row[parentLevel]);
          if (closestParent) summary.missing[level].add(row[closestParent].id);
        }
      });
    });

    const groups = new Map();
    rows.forEach((row) => {
      const key = row[frameworkGroupLevel]?.id || "unassigned";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return [...groups.entries()].flatMap(
      ([departmentKey, group], groupIndex) => {
        const isDepartmentCollapsed =
          collapsedCompanyDepartments[departmentKey] ?? !expandGroups;
        const summary = summaries.get(departmentKey);
        const departmentSummary = {
          counts: Object.fromEntries(
            levels.map((level) => [level, summary.counts[level].size]),
          ),
          missing: levels
            .filter((level) => summary.missing[level].size)
            .map((level) => ({
              level,
              count: summary.missing[level].size,
            })),
        };
        return (isDepartmentCollapsed ? group.slice(0, 1) : group).map(
          (row, index) => ({
            ...row,
            departmentKey,
            isAlternateGroup: groupIndex % 2 === 1,
            isDepartmentStart: index === 0,
            isDepartmentCollapsed,
            departmentSummary,
          }),
        );
      },
    );
  }

  function toggleCompanyDepartment(departmentKey) {
    collapsedCompanyDepartments = {
      ...collapsedCompanyDepartments,
      [departmentKey]: !(
        collapsedCompanyDepartments[departmentKey] ??
        companyFrameworkCompleteness !== "incomplete"
      ),
    };
    companyFrameworkPage = 1;
  }

  function openCompanyFrameworkAdd(type, row = null) {
    const parent = {
      Processes: ["Departments", row ? row.department?.id : selectedDepartment],
      Subprocesses: ["Processes", row ? row.process?.id : selectedProcess],
      Tasks: ["Subprocesses", row ? row.subprocess?.id : selectedSubprocess],
      Subtasks: ["Tasks", row ? row.task?.id : selectedTask],
    }[type];
    if (parent?.[1]) {
      openSubordinateModal(parent[0], { id: parent[1] });
    } else {
      openAddModal();
      selectedAddType = type;
      if (type === "Departments" && row?.process) {
        isSubordinateMode = true;
        selectedNextIds = [row.process.id];
      }
    }
  }

  function getCompanyMissingPathLevels(row, target) {
    const levels = ["department", "process", "subprocess", "task", "subtask"];
    const targetIndex = levels.indexOf(target);
    if (!row || targetIndex < 0) return [];
    if (target === "department") return row.department ? [] : [target];
    const closestParentIndex = levels
      .slice(0, targetIndex)
      .map((level, index) => (row[level] ? index : -1))
      .filter((index) => index >= 0)
      .pop();
    return levels.slice((closestParentIndex ?? -1) + 1, targetIndex + 1);
  }

  function getCompanyPathLabel(level) {
    return {
      department: "Department",
      process: "Process",
      subprocess: "Subprocess",
      task: "Task",
      subtask: "Subtask",
    }[level];
  }

  function getFrameworkModalNameDuplicate(type, value) {
    const name = value?.trim().toLocaleLowerCase();
    if (idproject || !type || !name) return false;

    const rows =
      {
        Departments: deptRows,
        Processes: processRows,
        Objects: objectRows,
        Subprocesses: subprocessesRows,
        Tasks: taskRows,
        Subtasks: subtaskRows,
      }[type] || [];
    const parentKey = {
      Subprocesses: "process_id",
      Tasks: "subprocess_id",
      Subtasks: "task_id",
    }[type];

    return rows.some((item) => {
      if (
        isEditMode &&
        selectedType === type &&
        String(item.id) === String(selectedObject?.id)
      )
        return false;
      if (parentKey && String(item[parentKey]) !== String(selectedPrevId))
        return false;
      return item.name?.trim().toLocaleLowerCase() === name;
    });
  }

  function getCompanyMissingPathRows(level) {
    let rows =
      {
        department: deptRows,
        process: processRows,
        subprocess: subprocessesRows,
        task: taskRows,
        subtask: subtaskRows,
      }[level] || [];

    const parentLevel = {
      process: "department",
      subprocess: "process",
      task: "subprocess",
      subtask: "task",
    }[level];
    if (!parentLevel) return rows;

    const parentId = getCompanyMissingPathSelectedId(parentLevel);
    if (!parentId) return [];
    if (level === "process") {
      const department = deptRows.find((item) => item.id === parentId);
      return rows.filter(
        (process) =>
          (process.departments || []).some(
            (item) => String(item.id) === String(parentId),
          ) ||
          (department?.processes || []).some(
            (item) => String(item.id) === String(process.id),
          ),
      );
    }

    const parentKey = {
      subprocess: "process_id",
      task: "subprocess_id",
      subtask: "task_id",
    }[level];
    return rows.filter((item) => String(item[parentKey]) === String(parentId));
  }

  function getCompanyMissingPathSelectedId(level) {
    if (!companyMissingPathLevels.includes(level)) {
      return companyMissingPathRow?.[level]?.id || "";
    }
    const name = companyMissingPathNames[level];
    return (
      findItemByName(getCompanyMissingPathRows(level), name || "")?.id || ""
    );
  }

  function getCompanyMissingPathOptions(level) {
    return Array.from(
      new Set(
        getCompanyMissingPathRows(level)
          .map((item) => item.name?.trim())
          .filter(Boolean),
      ),
    ).sort((first, second) => first.localeCompare(second));
  }

  function updateCompanyMissingPathName(level, value) {
    const levels = ["department", "process", "subprocess", "task", "subtask"];
    const levelIndex = levels.indexOf(level);
    companyMissingPathNames = Object.fromEntries(
      Object.entries({ ...companyMissingPathNames, [level]: value }).map(
        ([nameLevel, nameValue]) => [
          nameLevel,
          levels.indexOf(nameLevel) > levelIndex ? "" : nameValue,
        ],
      ),
    );
  }

  function findCompanyMissingPathExisting(level, name) {
    return findItemByName(getCompanyMissingPathRows(level), name);
  }

  async function linkExistingCompanyPath(ids) {
    const process = processRows.find((item) => item.id === ids.process);
    if (
      process &&
      ids.department &&
      !(process.departments || []).some(
        (department) => String(department.id) === String(ids.department),
      )
    ) {
      await updateCompanyPathElement("process", process, {
        departments: [
          ...(process.departments || []).map((department) => department.id),
          ids.department,
        ],
      });
    }

    const relations = [
      ["subprocess", subprocessesRows, "process_id", "process"],
      ["task", taskRows, "subprocess_id", "subprocess"],
      ["subtask", subtaskRows, "task_id", "task"],
    ];
    for (const [level, rows, parentKey, bodyKey] of relations) {
      const item = rows.find((row) => row.id === ids[level]);
      if (
        item &&
        ids[bodyKey] &&
        String(item[parentKey]) !== String(ids[bodyKey])
      ) {
        await updateCompanyPathElement(level, item, {
          [bodyKey]: ids[bodyKey],
        });
      }
    }
  }

  async function updateCompanyPathElement(level, item, relation) {
    const endpointByLevel = {
      process: "processes",
      subprocess: "subprocesses",
      task: "tasks",
      subtask: "subtasks",
    };
    const response = await fetchWithSession(
      `/api/damprojects/${endpointByLevel[level]}/${item.id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: item.name, ...relation }),
      },
    );
    const data = await response.json();
    if (!response.ok || data?.ok === false) {
      throw new Error(
        data?.error || data?.message || `Could not link ${level}.`,
      );
    }
  }

  function openCompanyFrameworkMissingPath(target, row) {
    const groupRows = companyFrameworkRows.filter(
      (candidate) =>
        (candidate[frameworkGroupLevel]?.id || "unassigned") ===
        row.departmentKey,
    );
    companyMissingPathRow =
      groupRows.find((candidate) => !candidate[target]) || row;
    companyMissingPathTarget = target;
    companyMissingPathNames = Object.fromEntries(
      getCompanyMissingPathLevels(companyMissingPathRow, target).map(
        (level) => [level, ""],
      ),
    );
    companyMissingPathError = "";
    openCompanyMissingPathModal = true;
  }

  async function createCompanyMissingPath() {
    if (!companyMissingPathCanSave || companyMissingPathSaving) return;

    const endpointByLevel = {
      department: "departments",
      process: "processes",
      subprocess: "subprocesses",
      task: "tasks",
      subtask: "subtasks",
    };
    const ids = Object.fromEntries(
      ["department", "process", "subprocess", "task", "subtask"].map(
        (level) => [level, companyMissingPathRow?.[level]?.id || ""],
      ),
    );

    companyMissingPathSaving = true;
    companyMissingPathError = "";
    try {
      for (const level of companyMissingPathLevels) {
        const name = companyMissingPathNames[level].trim();
        const existing = findCompanyMissingPathExisting(level, name);
        if (existing) {
          ids[level] = existing.id;
          continue;
        }

        const body = { name };
        if (level === "department")
          body.processes = ids.process ? [ids.process] : [];
        if (level === "process")
          body.departments = ids.department ? [ids.department] : [];
        if (level === "subprocess") body.process = ids.process;
        if (level === "task") body.subprocess = ids.subprocess;
        if (level === "subtask") body.task = ids.task;

        const response = await fetchWithSession(
          `/api/damprojects/${endpointByLevel[level]}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
        );
        const data = await response.json();
        const created = data?.data || data;
        if (!response.ok || data?.ok === false || !created?.id) {
          throw new Error(
            data?.error || data?.message || `Could not create ${level}.`,
          );
        }
        ids[level] = created.id;
      }
      await linkExistingCompanyPath(ids);
      await refreshTables();
      openCompanyMissingPathModal = false;
    } catch (error) {
      companyMissingPathError =
        error.message || "Could not complete the missing path.";
    } finally {
      companyMissingPathSaving = false;
    }
  }

  function deleteCompanyFrameworkElement(type, object) {
    selectedType = type;
    selectedObject = object;
    frameworkDeleteError = "";
    openDangerModal = true;
  }

  async function createCompanyFrameworkEntry() {
    if (!companyQuickAddAllowed || companyQuickSaving) return;

    companyQuickSaving = true;
    companyQuickError = "";
    try {
      let subprocessId = companyQuickSubprocessId;
      let taskId = companyQuickTaskId;
      let createdEntry = false;

      if (companyQuickSubprocess && !subprocessId) {
        const response = await fetchWithSession(
          "/api/damprojects/subprocesses",
          {
            method: "POST",
            body: JSON.stringify({
              name: companyQuickSubprocess,
              process: companyQuickProcessId,
            }),
          },
        );
        const data = await response.json();
        if (!response.ok || !data.ok)
          throw new Error(data.error || data.message);

        await getSubprocesses();
        subprocessId = subprocessesRows.find(
          (subprocess) =>
            subprocess.process_id === companyQuickProcessId &&
            subprocess.name.trim().toLocaleLowerCase() ===
              companyQuickSubprocess.trim().toLocaleLowerCase(),
        )?.id;
        createdEntry = true;
      }

      if (companyQuickTask && !taskId) {
        if (!subprocessId) {
          throw new Error("Enter a subprocess before adding a task.");
        }
        const response = await fetchWithSession("/api/damprojects/tasks", {
          method: "POST",
          body: JSON.stringify({
            name: companyQuickTask,
            subprocess: subprocessId,
          }),
        });
        const data = await response.json();
        if (!response.ok || !data.ok)
          throw new Error(data.error || data.message);

        await getTasks();
        taskId = taskRows.find(
          (task) =>
            task.subprocess_id === subprocessId &&
            task.name.trim().toLocaleLowerCase() ===
              companyQuickTask.trim().toLocaleLowerCase(),
        )?.id;
        createdEntry = true;
      }

      if (companyQuickSubtask) {
        if (!taskId) {
          throw new Error("Enter a task before adding a subtask.");
        }
        const existingSubtask = findItemByName(
          subtaskRows,
          companyQuickSubtask,
          "task_id",
          taskId,
        );
        if (!existingSubtask) {
          const response = await fetchWithSession("/api/damprojects/subtasks", {
            method: "POST",
            body: JSON.stringify({ name: companyQuickSubtask, task: taskId }),
          });
          const data = await response.json();
          if (!response.ok || !data.ok)
            throw new Error(data.error || data.message);
          createdEntry = true;
        }
      }

      if (createdEntry) await refreshTables();
      companyQuickSubprocess = "";
      companyQuickTask = "";
      companyQuickSubtask = "";
      companyFrameworkPage = 1;
    } catch (error) {
      companyQuickError =
        error?.message ||
        "Could not save the framework entry. Please try again.";
    } finally {
      companyQuickSaving = false;
    }
  }

  function persistCompanyInfo() {
    const payload = {
      ...companyInfo,
      industry: companyInfo.industries.join(", "),
      businessModel: companyInfo.businessModels.join(", "),
      operatingRegions: companyInfo.regions.join(", "),
      productsAndServices: companyInfo.offerings.join(", "),
      customerSegments: companyInfo.customerSegmentsSelected.join(", "),
      strategicPriorities: companyInfo.strategicPrioritiesSelected.join(", "),
      currentChallenges: companyInfo.keyChallenges.join(", "),
      coreSystems: companyInfo.systemsTools.join(", "),
      constraints: companyInfo.restrictedAssumptions.join(", "),
    };
    localStorage.setItem(companyInfoStorageKey, JSON.stringify(payload));
  }

  $: if (companyInfoLoaded && companyInfo) {
    clearTimeout(companyInfoSaveTimer);
    companyInfoSaveTimer = setTimeout(persistCompanyInfo, 500);
  }

  function toggleCompanyInfoChoice(field, value, multiple = true) {
    if (!multiple) {
      companyInfo = { ...companyInfo, [field]: value };
      return;
    }

    const selected = Array.isArray(companyInfo[field])
      ? companyInfo[field]
      : [];
    companyInfo = {
      ...companyInfo,
      [field]: selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    };
  }

  function handleCompanyInfoChange() {
    companyInfo = { ...companyInfo };
  }

  function normalizeCompanyInfo(saved) {
    const defaults = defaultCompanyInfo();
    const source = saved && typeof saved === "object" ? saved : {};
    const normalized = { ...defaults, ...source };
    delete normalized.taskMiningWorkTypes;
    delete normalized.taskMiningAnalysisGoals;
    if (typeof source.useOrganizationInfoInAiAnalyses !== "boolean") {
      normalized.useOrganizationInfoInAiAnalyses =
        typeof source.useInAiAnalyses === "boolean"
          ? source.useInAiAnalyses
          : defaults.useOrganizationInfoInAiAnalyses;
    }
    if (typeof source.useContextObjectivesInAiAnalyses !== "boolean") {
      normalized.useContextObjectivesInAiAnalyses =
        typeof source.useInAiAnalyses === "boolean"
          ? source.useInAiAnalyses
          : defaults.useContextObjectivesInAiAnalyses;
    }
    [
      "regions",
      "industries",
      "businessModels",
      "offerings",
      "customerSegmentsSelected",
      "strategicPrioritiesSelected",
      "keyChallenges",
      "restrictedAssumptions",
      "systemsTools",
    ].forEach((field) => {
      if (!Array.isArray(normalized[field])) {
        normalized[field] = normalized[field]
          ? String(normalized[field])
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : [];
      }
    });

    if (!normalized.regions.length && source.operatingRegions) {
      normalized.regions = source.operatingRegions
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (!normalized.industries.length && source.industry) {
      normalized.industries = [source.industry];
    }
    if (!normalized.businessModels.length && source.businessModel) {
      normalized.businessModels = [source.businessModel];
    }
    if (!normalized.offerings.length && source.productsAndServices) {
      normalized.offerings = source.productsAndServices
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (
      !normalized.customerSegmentsSelected.length &&
      source.customerSegments
    ) {
      normalized.customerSegmentsSelected = source.customerSegments
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (
      !normalized.strategicPrioritiesSelected.length &&
      source.strategicPriorities
    ) {
      normalized.strategicPrioritiesSelected = source.strategicPriorities
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (!normalized.keyChallenges.length && source.currentChallenges) {
      normalized.keyChallenges = source.currentChallenges
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (!normalized.systemsTools.length && source.coreSystems) {
      normalized.systemsTools = source.coreSystems
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (!normalized.restrictedAssumptions.length && source.constraints) {
      normalized.restrictedAssumptions = source.constraints
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (!normalized.companySize && source.employeeCount) {
      normalized.companySize = String(source.employeeCount);
    }

    const legacyCompanySizeMap = {
      "1-50": "<250",
      "51-200": "<250",
      "201-500": "251-1.000",
      "501-1,000": "251-1.000",
      "1,000+": "1.000-5.000",
    };
    normalized.companySize =
      legacyCompanySizeMap[normalized.companySize] || normalized.companySize;

    const allowedCompanyInfoChoices = {
      industries: companyInfoChoices.industry,
      businessModels: companyInfoChoices.businessModel,
      regions: companyInfoChoices.regions,
      offerings: companyInfoChoices.offerings,
      customerSegmentsSelected: companyInfoChoices.customerSegments,
      strategicPrioritiesSelected: companyInfoChoices.strategicPriorities,
      keyChallenges: companyInfoChoices.keyChallenges,
      systemsTools: companyInfoChoices.targetAreas,
    };
    Object.entries(allowedCompanyInfoChoices).forEach(([field, choices]) => {
      normalized[field] = normalized[field].filter((value) =>
        choices.includes(value),
      );
    });

    return normalized;
  }

  function buildOrgFrameworkRows(departments) {
    const sortedRows = departments
      .flatMap((department) => {
        const areas = department.areas?.length ? department.areas : [null];
        return areas.map((area) => ({
          id: `${department.id}:${area?.id || ""}`,
          businessUnit: department.business_unit,
          department,
          area,
        }));
      })
      .sort((first, second) =>
        [first.businessUnit?.name, first.department?.name, first.area?.name]
          .map((value) => value || "")
          .join("\u0000")
          .localeCompare(
            [
              second.businessUnit?.name,
              second.department?.name,
              second.area?.name,
            ]
              .map((value) => value || "")
              .join("\u0000"),
          ),
      );

    return assignGroupColors(
      sortedRows,
      (row) => row.businessUnit?.id || row.department.id,
    );
  }

  function getVisibleOrgFrameworkRows(rows) {
    let previousBusinessUnitKey = null;

    return rows.reduce((visibleRows, row) => {
      const businessUnitKey = row.businessUnit?.id || row.department.id;
      const isBusinessUnitStart = businessUnitKey !== previousBusinessUnitKey;
      previousBusinessUnitKey = businessUnitKey;

      const isBusinessUnitCollapsed = Boolean(
        collapsedOrgBusinessUnits[businessUnitKey],
      );

      if (isBusinessUnitStart || !isBusinessUnitCollapsed) {
        visibleRows.push({
          ...row,
          businessUnitKey,
          isBusinessUnitStart,
          isBusinessUnitCollapsed,
        });
      }

      return visibleRows;
    }, []);
  }

  function toggleOrgBusinessUnit(businessUnitKey) {
    collapsedOrgBusinessUnits = {
      ...collapsedOrgBusinessUnits,
      [businessUnitKey]: !collapsedOrgBusinessUnits[businessUnitKey],
    };
  }

  function mergeOrgDepartmentsIntoProcessFramework() {
    if (!orgDepartments.length) return;

    const existingIds = new Set(deptRows.map((department) => department.id));
    const existingNames = new Set(
      deptRows.map((department) => department.name?.trim().toLocaleLowerCase()),
    );
    const organizationalDepartments = orgDepartments
      .filter(
        (department) =>
          !existingIds.has(department.id) &&
          !existingNames.has(department.name?.trim().toLocaleLowerCase()),
      )
      .map((department) => ({
        ...department,
        processes: department.processes || [],
        isOrganizationOnly: true,
      }));

    if (organizationalDepartments.length) {
      deptRows = [...deptRows, ...organizationalDepartments];
    }
  }

  async function syncOrgDepartmentsIntoProcessFramework() {
    const departmentNames = new Set(
      deptRows
        .filter((department) => !department.isOrganizationOnly)
        .map((department) => department.name?.trim().toLocaleLowerCase()),
    );
    const missingDepartments = orgDepartments.filter(
      (department) =>
        department.name &&
        !departmentNames.has(department.name.trim().toLocaleLowerCase()),
    );

    for (const department of missingDepartments) {
      const response = await fetchWithSession("/api/damprojects/departments", {
        method: "POST",
        body: JSON.stringify({ name: department.name.trim() }),
      });
      const data = await response.json();
      if (!response.ok || data.error || data.ok === false) {
        throw new Error(
          data.error || data.message || "Could not synchronize department.",
        );
      }
      departmentNames.add(department.name.trim().toLocaleLowerCase());
    }

    if (missingDepartments.length) await getDepartments();
  }

  async function getOrgFramework() {
    orgFrameworkLoading = true;
    orgFrameworkError = "";

    try {
      const [businessUnitResponse, departmentResponse, areaResponse] =
        await Promise.all([
          fetchWithSession("/api/knextractor/business-units/"),
          fetchWithSession("/api/knextractor/departments"),
          fetchWithSession("/api/knextractor/areas"),
        ]);
      if (
        !businessUnitResponse.ok ||
        !departmentResponse.ok ||
        !areaResponse.ok
      ) {
        throw new Error("Could not load organizational data.");
      }

      orgBusinessUnits = await businessUnitResponse.json();
      orgDepartments = await departmentResponse.json();
      orgAreas = await areaResponse.json();
      mergeOrgDepartmentsIntoProcessFramework();
      await syncOrgDepartmentsIntoProcessFramework();
      orgFrameworkRows = buildOrgFrameworkRows(orgDepartments);
    } catch (error) {
      orgFrameworkError =
        error.message || "Could not load organizational data.";
    } finally {
      orgFrameworkLoading = false;
    }
  }

  function getOrgTypeLabel(type) {
    return {
      "Business-Units": "Business Unit",
      Departments: "Department",
      Areas: "Area",
    }[type];
  }

  function getOrgParentOptions(type) {
    if (type === "Departments") return orgBusinessUnits;
    if (type === "Areas") return orgDepartments;
    return [];
  }

  function getOrgParentId(object, type) {
    if (type === "Departments") return getOrgBusinessUnitId(object);
    if (type === "Areas") return getOrgDepartmentId(object);
    return "";
  }

  function openOrgAddModal(type) {
    orgModalMode = "add";
    orgModalType = type;
    orgModalName = "";
    orgModalParentId = getOrgParentOptions(type)[0]?.id || "";
    selectedOrgObject = null;
    openOrgModal = true;
  }

  function openOrgEditModal(type, row) {
    const object =
      type === "Business-Units"
        ? row.businessUnit
        : type === "Departments"
          ? row.department
          : row.area;
    if (!object) return;
    orgModalMode = "edit";
    orgModalType = type;
    orgModalName = object.name || "";
    orgModalParentId = getOrgParentId(object, type);
    selectedOrgObject = object;
    openOrgModal = true;
  }

  function openOrgDeleteModal(type, row) {
    selectedOrgObject =
      type === "Business-Units"
        ? row.businessUnit
        : type === "Departments"
          ? row.department
          : row.area;
    if (!selectedOrgObject) return;
    orgModalType = type;
    openOrgDangerModal = true;
  }

  async function saveOrgElement() {
    if (!orgModalName.trim()) return;
    const endpointType = orgModalType.toLowerCase();
    const body = { name: orgModalName.trim() };
    if (orgModalType === "Departments" && orgModalParentId) {
      body.business_unit = orgModalParentId;
    }
    if (orgModalType === "Areas" && orgModalParentId) {
      body.department = orgModalParentId;
    }

    const endpoint = `/api/knextractor/${endpointType}${
      orgModalMode === "edit" ? `/${selectedOrgObject.id}` : ""
    }`;
    const response = await fetchWithSession(endpoint, {
      method: orgModalMode === "edit" ? "PATCH" : "POST",
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (data.ok) {
      openOrgModal = false;
      await getOrgFramework();
    }
  }

  async function deleteOrgElement() {
    if (!selectedOrgObject) return;
    const endpoint = `/api/knextractor/${orgModalType.toLowerCase()}/${selectedOrgObject.id}`;
    const response = await fetchWithSession(endpoint, { method: "DELETE" });
    const data = await response.json();
    openOrgDangerModal = false;
    if (data.ok) await getOrgFramework();
  }

  onMount(async () => {
    const savedCompanyInfo = localStorage.getItem(companyInfoStorageKey);
    if (savedCompanyInfo) {
      try {
        companyInfo = normalizeCompanyInfo(JSON.parse(savedCompanyInfo));
      } catch {
        localStorage.removeItem(companyInfoStorageKey);
      }
    }
    companyInfoLoaded = true;
    fetchWithSession(`/api/damprojects/projects`)
      .then((response) => response.json())
      .then((data) => {
        const allProjects = Object.values(data)
          .filter((value) => Array.isArray(value))
          .flat();
        projects = Array.from(
          new Map(
            allProjects.map((item) => [
              item.id,
              { id: item.id, name: item.name },
            ]),
          ).values(),
        );
      });
    await refreshTables();
    frameworkDataLoaded = true;
    await getOrgFramework();
  });

  onDestroy(() => clearTimeout(companyInfoSaveTimer));

  $$restProps;

  // $: console.log({
  //   listOfDepartments,
  //   listOfProcesses,
  //   listOfSubprocesses,
  //   listOfTasks,
  //   deptRows,
  //   processRows,
  //   subprocessesRows,
  //   taskRows,
  //   subtaskRows,
  //   objectRows,
  // });
</script>

<Header
  section={idproject
    ? "Define Project Scope"
    : companyStructureOptions[selectedCompanyStructureView]}
  area={idproject ? "Setup" : "Company Settings"}
  areaHref={idproject ? "" : ROUTES.dam.companySettings.companyInfo}
  breadcrumb={idproject
    ? "Define Project Scope"
    : companyStructureOptions[selectedCompanyStructureView]}
  tooltipText={idproject
    ? 'Select the subtasks for this project\'s data collection via "In Scope" (saved automatically) or build the framework with the Framework Assistant. "Complete & Lock Framework" finishes this step. Afterwards, the framework cannot be changed.'
    : "Define the company-wide framework, including departments, processes, subprocesses, tasks, subtasks, and business objects."}
>
  <div class="framework-header-actions">
    {#if !idproject && selectedCompanyStructureView === 2 && $uiFeatureStore.platformFeatures.frameworkAssistant}
      <Button
        kind="secondary"
        size="field"
        text="Open Framework Assistant"
        iconLeft={true}
        icon={ROBOT_2_ICON}
        on:click={openFrameworkAssistant}
      />
      <Button
        kind="secondary"
        size="field"
        text="Import from Excel"
        on:click={handleOpenImport}
      />
    {/if}
    <!--
  {#if idproject && $role?.role === "admin"}
    <Button
      kind="secondary"
      text={showLegacyProjectFramework ? "New View" : "Old View"}
      size="field"
      on:click={() => {
        resetFilters();
        showLegacyProjectFramework = !showLegacyProjectFramework;
      }}
    />
  {/if}
  -->
    {#if idproject}
      <div data-guide="complete-project-framework">
        <ProjectStepActions
          {idproject}
          {step}
          {stage}
          showGoBackButton={false}
          allowAdminOverride={false}
          disabled={!collectionReady ||
            collectionSaving ||
            collectionSelectedCount === 0 ||
            Boolean(collectionStorageError)}
          text="Complete & Lock Framework"
          size="sm"
          completionWarning="After completion, the framework settings cannot be changed. Please make sure everything is configured correctly before continuing. This action cannot be undone."
        />
      </div>
    {/if}
  </div>
</Header>
<SectionWrapper
  fullscreen={true}
  seamless={true}
  wrapperClass={idproject && !showLegacyProjectFramework
    ? "project-framework-wrapper"
    : ""}
  seamlessPadding={idproject && showLegacyProjectFramework}
  loading={!frameworkDataLoaded || orgFrameworkLoading}
  loadingTitle="Loading framework."
  loadingHint="Please wait while the framework data is prepared."
>
  <div
    class="main"
    class:project-framework-new={idproject && !showLegacyProjectFramework}
    class:project-framework-legacy={idproject && showLegacyProjectFramework}
    slot="main"
  >
    {#if idproject}
      <div
        class="project-framework-notices"
        class:project-framework-intro={!showLegacyProjectFramework}
      >
        {#if showLegacyProjectFramework}
          <InfoBlock
            type="info"
            title="Configure Framework"
            heading='Define the project framework here. When finished, click "Complete & Lock Framework" to continue. The framework cannot be changed after locking.'
            showClose={false}
            dismissKey="configure-framework-info"
            showDismissButton={false}
          />
        {:else}
          <!-- Eine Zeile mit dem Stand; aufgebaut wird im Framework Assistant (gefuehrt, mit den Team-Umfragen) -->
          <!-- Ohne Processes nur der Hinweis darunter; die Komponente bleibt eingehaengt und liefert die Departments -->
          <FrameworkInfoRow
            {idproject}
            frameworkRows={companyFrameworkRows}
            frameworkDataLoaded={frameworkDataLoaded &&
              companyFrameworkRows.some((row) => row.process)}
            assistantEnabled={$uiFeatureStore.platformFeatures
              .frameworkAssistant}
            on:loaded={frameworkInfoLoaded}
          >
            <svelte:fragment slot="actions">
              <div data-guide="project-framework-assistant">
                <ProjectStepActions
                  {idproject}
                  {step}
                  {stage}
                  disabled={frameworkHasBlockingIssues}
                  showGoBackButton={false}
                  showCompletedState={false}
                  confirmBeforeComplete={false}
                  onClick={() => openGuidedAssistant()}
                  iconLeft={true}
                  icon={ROBOT_2_ICON}
                  text="Start Framework Assistant"
                  size="sm"
                />
              </div>
            </svelte:fragment>
          </FrameworkInfoRow>
        {/if}
        {#if frameworkDataLoaded && idproject && projectDepartmentIds.length && !companyFrameworkRows.some((row) => row.process)}
          <InfoBlock
            type="info"
            heading="No processes for this department yet"
            message={$uiFeatureStore.platformFeatures.frameworkAssistant
              ? "Add processes directly in the table below or build the framework with guided help in the Framework Assistant."
              : "Add processes directly in the table below."}
            showClose={false}
            showActions={$uiFeatureStore.platformFeatures.frameworkAssistant}
          >
            <svelte:fragment slot="actions">
              <Button
                text="Start Framework Assistant"
                kind="secondary"
                icon={ROBOT_2_ICON}
                iconLeft={true}
                size="field"
                on:click={openGuidedAssistant}
              />
            </svelte:fragment>
          </InfoBlock>
        {/if}
        {#if frameworkDataLoaded && idproject && !projectDepartmentIds.length}
          <InfoBlock
            type="warning"
            heading="No department connected to this project"
            message="The project framework only shows processes linked to the project's department."
            showClose={false}
          />
        {/if}
        {#if showLegacyProjectFramework && frameworkDataLoaded && departmentAssignmentMissing}
          <InfoBlock
            type="warning"
            title="No Department connected to current project"
            heading="Please connect at least one department to the current project before completing the framework."
            showClose={false}
          />
        {/if}
        {#each frameworkDataLoaded && showLegacyProjectFramework ? pagedTypes.filter((type) => type.missing.length || (type.type === "Departments" && departmentAssignmentMissing)) : [] as errorType}
          <InfoBlock
            type="warning"
            title={errorType.type}
            heading={getFrameworkNoticeMessage(errorType.type, errorType.rows)}
            showClose={false}
          />
        {/each}
      </div>
    {/if}
    {#if !idproject || !showLegacyProjectFramework}
      {#if !idproject && selectedCompanyStructureView === 0}
        <CompanyInfo
          {companyInfo}
          {companyInfoChoices}
          {toggleCompanyInfoChoice}
          onCompanyInfoChange={handleCompanyInfoChange}
        />
      {:else if !idproject && selectedCompanyStructureView === 1}
        <OrganizationFramework
          {listOfOrgBusinessUnits}
          {listOfOrgDepartments}
          {listOfOrgAreas}
          bind:selectedOrgBusinessUnit
          bind:selectedOrgDepartment
          bind:selectedOrgArea
          {orgFilterKey}
          {orgFrameworkLoading}
          {orgFrameworkError}
          {orgFrameworkPagedRows}
          {visibleOrgFrameworkRows}
          bind:orgFrameworkPage
          bind:orgFrameworkPageSize
          {resetOrgFilters}
          {openOrgAddModal}
          {toggleOrgBusinessUnit}
          {openOrgEditModal}
          {openOrgDeleteModal}
        />
      {:else}
        <ProcessFramework
          projectId={idproject}
          {companyFrameworkRows}
          defaultDepartmentIds={projectDepartmentIds}
          defaultScopeReady={frameworkDataLoaded && !orgFrameworkLoading}
          bind:collectionSelectedCount
          bind:collectionStorageError
          bind:collectionReady
          bind:collectionSaving
          {listOfDepartments}
          {listOfProcesses}
          {listOfSubprocesses}
          {listOfTasks}
          bind:selectedDepartment
          bind:selectedProcess
          bind:selectedSubprocess
          bind:selectedTask
          {filterKey}
          {companyFrameworkPagedRows}
          {visibleCompanyFrameworkRows}
          {companyFrameworkIssues}
          bind:companyFrameworkSearch
          bind:companyFrameworkCompleteness
          bind:companyFrameworkSortKey
          bind:companyFrameworkSortDirection
          bind:companyFrameworkPage
          bind:companyFrameworkPageSize
          {handleOpenImport}
          showImportButton={!idproject &&
            !$uiFeatureStore.platformFeatures.frameworkAssistant}
          {resetFilters}
          {openCompanyFrameworkAdd}
          {openCompanyFrameworkMissingPath}
          {toggleCompanyDepartment}
          editFrameworkElement={openEditModal}
          deleteFrameworkElement={deleteCompanyFrameworkElement}
          addFrameworkChild={openSubordinateModal}
        />
      {/if}
    {:else}
      <div class="options-container">
        <div class="switcher-wrapper">
          <ContentSwitcher
            bind:selected={selectedProcessView}
            options={["Process Framework", "Process Objects"]}
            fitContent={true}
          ></ContentSwitcher>
          {#if !selectedProcessView}
            <ContentSwitcher
              bind:selected={selectedTableView}
              options={tableViewOptions}
              fitContent={true}
            ></ContentSwitcher>
          {/if}
        </div>
        <!-- Secondary hierarchy filters are currently not needed here.
    <div class="filter-wrapper">
      {#if !selectedProcessView}
        {#key filterKey}
          <div class="filter-field">
            <span class="filter-label">Departments</span>
            <Select
              items={listOfDepartments.map((option) => ({ value: option.key, text: option.value }))}
              bind:selected={selectedDepartment}
              color="dark"
              labelText="Departments"
              placeholder="Select a department"
            />
          </div>
          <div class="filter-field">
            <span class="filter-label">Processes</span>
            <Select
              items={listOfProcesses.map((option) => ({ value: option.key, text: option.value }))}
              bind:selected={selectedProcess}
              color="dark"
              labelText="Processes"
              placeholder="Select a process"
            />
          </div>
          <div class="filter-field">
            <span class="filter-label">Subprocesses</span>
            <Select
              items={listOfSubprocesses.map((option) => ({ value: option.key, text: option.value }))}
              bind:selected={selectedSubprocess}
              color="dark"
              labelText="Subprocesses"
              placeholder="Select a subprocess"
            />
          </div>
          <div class="filter-field">
            <span class="filter-label">Tasks</span>
            <Select
              items={listOfTasks.map((option) => ({ value: option.key, text: option.value }))}
              bind:selected={selectedTask}
              color="dark"
              labelText="Tasks"
              placeholder="Select a task"
            />
          </div>
        {/key}
      {:else}
        {#key filterKey}
          <div class="filter-field">
            <span class="filter-label">Department</span>
            <Select
              items={listOfDepartments.map((option) => ({ value: option.key, text: option.value }))}
              bind:selected={selectedDepartment}
              color="dark"
              labelText="Department"
              placeholder="Select a department"
            />
          </div>
          <div class="filter-field">
            <span class="filter-label">Process</span>
            <Select
              items={listOfProcesses.map((option) => ({ value: option.key, text: option.value }))}
              bind:selected={selectedProcess}
              color="dark"
              labelText="Process"
              placeholder="Select a process"
            />
          </div>
        {/key}
      {/if}
      <Button
        icon={RESET_ICON}
        medium
        on:click={() => {
          resetFilters();
        }}
      />
    </div>
    -->
      </div>
      {#each pagedTypes as type}
        {#if selectedProcessView === 1 ? ["Departments", "Processes", "Objects"].includes(type.type) : selectedTableView === 0 ? type.type !== "Objects" : type.type === tableViewOptions[selectedTableView]}
          {#if selectedProcessView === 0 && tableViewOptions[selectedTableView] === "All"}
            <InfoBlock
              type="info"
              heading={type.type}
              message={getFrameworkInfo(type.type)}
              showClose={false}
            />
          {/if}
          <div
            class={selectedProcessView === 0 &&
            tableViewOptions[selectedTableView] === "All"
              ? "table-container all-table-container"
              : "table-container"}
          >
            <DataTableBlack
              headers={type.headers}
              rows={type.pagedRows}
              totalItems={type.filteredCount}
              searchValue={searchTerms[type.type]}
              newButtonText="New {getNameByType(type.type)}"
              bind:page={paginations[type.type].page}
              bind:pageSize={paginations[type.type].pageSize}
              on:search={(e) => {
                searchTerms[type.type] = e.detail.value;
                searchTerms = searchTerms;
                paginations[type.type].page = 1;
                paginations = paginations;
              }}
              on:clearSearch={() => {
                searchTerms[type.type] = "";
                searchTerms = searchTerms;
                paginations[type.type].page = 1;
                paginations = paginations;
              }}
              on:addNew={() => {
                openAddModal();
                selectedAddType = type.type;
              }}
              on:edit={(e) => openEditModal(type.type, e.detail.row)}
              on:action={(e) => openSubordinateModal(type.type, e.detail.row)}
              on:delete={(e) => {
                selectedType = type.type;
                selectedObject = e.detail.row;
                frameworkDeleteError = "";
                openDangerModal = true;
              }}
            />
          </div>
        {/if}
      {/each}
    {/if}
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showFrameworkGuide}
  bind:currentStep={frameworkGuideStep}
  steps={frameworkGuideSteps}
  allowInteraction={false}
/>

{#if openDangerModal}
  <DangerModal
    danger
    bind:open={openDangerModal}
    preventCloseOnClickOutside
    modalHeading="Delete"
    primaryButtonText={frameworkDeleteSaving ? "Deleting…" : "Delete"}
    primaryButtonDisabled={frameworkDeleteSaving ||
      isOrgManagedDepartment(selectedType, selectedObject)}
    secondaryButtonText="Cancel"
    on:click:button--primary={deleteSelected}
    on:click:button--secondary={() => (openDangerModal = false)}
  >
    <div class="deleteModalBody">
      {#if isOrgManagedDepartment(selectedType, selectedObject)}
        <p>
          <span class="project-name">{selectedObject?.name}</span> comes from
          <strong>Company Settings → Organization Framework</strong>. As long as
          it exists there, it will automatically appear in the Process
          Framework.
        </p>
        <p>
          <a
            href="/app/dam/company-settings/organization-framework"
            target="_blank"
            rel="noopener noreferrer"
            >Open Organization Framework in a new tab</a
          > to delete it and review any dependent areas.
        </p>
      {:else}
        <p>
          Are you sure you want to delete this {typeOptions
            .find((type) => type.id === selectedType)
            .text.toLowerCase()}:
          <span class="project-name">{selectedObject?.name}</span>?
        </p>
        {#each getFrameworkDeleteImpact(selectedType, selectedObject) as impact}
          <div class="delete-impact-group">
            <p>The associated {impact.label} will also be deleted:</p>
            <ul class="delete-impact-chips" aria-label={impact.label}>
              {#each impact.items as item}
                <li><Tag type="gray">{item.name}</Tag></li>
              {/each}
            </ul>
          </div>
        {/each}
        <p class="delete-warning">This action cannot be undone.</p>
      {/if}
    </div>
    {#if frameworkDeleteError}<p class="company-missing-path-error">
        {frameworkDeleteError}
      </p>{/if}
  </DangerModal>
{/if}

<Modal
  modalHeading="Start a new guided setup?"
  bind:open={openReplaceAssistantDraftModal}
  actions
  autowidth
  noSecondaryButton
  modalClass="replace-assistant-draft-modal"
  primaryButtonText="Replace draft and start"
  primaryButtonVariant="secondary"
  primaryButtonAction={startGuidedAssistant}
>
  <div class="replace-assistant-draft-copy">
    <p>
      Starting a new guided setup will replace your current Framework Assistant
      draft.
    </p>
    <p>Your project framework will stay unchanged.</p>
  </div>
  <Button
    slot="left-buttons"
    kind="secondary"
    text="Cancel"
    on:click={() => (openReplaceAssistantDraftModal = false)}
  />
</Modal>

<Modal
  modalHeading={companyMissingPathSingleLabel
    ? `Add New ${companyMissingPathSingleLabel}`
    : "Complete missing path"}
  subtitle={companyMissingPathSubtitle}
  bind:open={openCompanyMissingPathModal}
  actions
  autowidth
  preventCloseOnClickOutside
  noSecondaryButton
  modalClass="framework-element-modal"
  primaryButtonText={companyMissingPathSaving
    ? "Saving…"
    : companyMissingPathSingleLabel
      ? "Save"
      : "Complete path"}
  primaryButtonVariant="secondary"
  primaryButtonAction={createCompanyMissingPath}
  primaryButtonNotAllow={!companyMissingPathCanSave || companyMissingPathSaving}
  primaryButtonValidations={!companyMissingPathCanSave ||
    companyMissingPathSaving}
  secondaryButtonAction={() => (openCompanyMissingPathModal = false)}
>
  <div class="framework-modal-fields">
    {#if departmentFrameworkNotice}
      <InfoBlock
        type="info"
        heading="Department framework"
        message={departmentFrameworkNotice}
        showClose={false}
      />
    {/if}
    <div class="company-missing-path-context">
      {#if companyMissingPathRow?.department}<span
          >Department: {companyMissingPathRow.department.name}</span
        >{/if}
      {#if companyMissingPathRow?.process}<span
          >Process: {companyMissingPathRow.process.name}</span
        >{/if}
      {#if companyMissingPathRow?.subprocess}<span
          >Subprocess: {companyMissingPathRow.subprocess.name}</span
        >{/if}
      {#if companyMissingPathRow?.task}<span
          >Task: {companyMissingPathRow.task.name}</span
        >{/if}
    </div>
    {#each companyMissingPathLevels as level}
      <TextInput
        id={`company-missing-path-${level}`}
        labelText={`${getCompanyPathLabel(level)} name`}
        placeholder={`Select or enter ${getCompanyPathLabel(level).toLowerCase()}`}
        list={`company-missing-path-${level}-options`}
        value={companyMissingPathNames[level]}
        on:input={(event) => updateCompanyMissingPathName(level, event.detail)}
      />
      <datalist id={`company-missing-path-${level}-options`}>
        {#each getCompanyMissingPathOptions(level) as option}
          <option value={option} />
        {/each}
      </datalist>
    {/each}
    {#if companyMissingPathError}<p class="company-missing-path-error">
        {companyMissingPathError}
      </p>{/if}
  </div>
  <Button
    slot="left-buttons"
    kind="secondary"
    text="Cancel"
    on:click={() => (openCompanyMissingPathModal = false)}
  />
</Modal>

<Modal
  modalHeading={isEditMode
    ? `Edit ${getNameByType(selectedType)}`
    : `Add New ${selectedAddType ? getNameByType(selectedAddType) : "Element"}${
        projectScopeActive &&
        selectedAddType === "Processes" &&
        projectDepartmentRows.length
          ? ` (Department: ${projectDepartmentRows.map((department) => department.name).join(", ")})`
          : ""
      }`}
  bind:open={openModalAdd}
  actions
  autowidth
  preventCloseOnClickOutside
  noSecondaryButton
  modalClass="framework-element-modal"
  primaryButtonText={frameworkModalSaving ? "Saving…" : "Save"}
  primaryButtonVariant="secondary"
  on:click:button--secondary={() => (openModalAdd = false)}
  primaryButtonAction={selectedType ? updateElement : createElement}
  primaryButtonNotAllow={isPrimaryButtonNotAllowed}
  primaryButtonValidations={isPrimaryButtonNotAllowed}
  on:submit={() => {
    selectedType = "";
    selectedAddType = "";
    openModalAdd = false;
  }}
  on:close={cleanModal}
>
  <!-- Typ wird beim Öffnen bereits gesetzt (Add New / Subordinate) -->
  <!-- {#if !isEditMode}
    <Select labelText="Select Type" bind:selected={selectedAddType}>
      <SelectItem value="" text="" />
      {#each typeOptions as option}
        <SelectItem value={option.id} text={option.text} />
      {/each}
    </Select>
    <br />
  {/if} -->

  {#if (isEditMode && selectedType) || (!isEditMode && selectedAddType)}
    <div class="framework-modal-fields">
      {#if departmentFrameworkNotice}
        <InfoBlock
          type="info"
          heading="Department framework"
          message={departmentFrameworkNotice}
          showClose={false}
        />
      {/if}
      <TextInput
        labelText="{isEditMode
          ? getNameByType(selectedType)
          : getNameByType(selectedAddType)} name"
        placeholder="Enter {isEditMode
          ? getNameByType(selectedType)
          : getNameByType(selectedAddType)} name"
        bind:value={newName}
        invalid={frameworkNameValidation.duplicate}
        invalidText={`A ${getNameByType(isEditMode ? selectedType : selectedAddType).toLowerCase()} with this name already exists.`}
        clearInvalidOnFocus={false}
      />

      {#if (isEditMode && selectedType === "Departments") || (!isEditMode && selectedAddType === "Departments")}
        {#key previousToSelect}
          <MultiSelect
            titleText="Select Projects"
            bind:selectedIds={selectedPrevIds}
            label={selectedPrevIds.length > 0
              ? setSelectedValues(selectedPrevIds, previousToSelect)
              : "Select Projects"}
            items={previousToSelect}
          />
          <MultiSelect
            titleText="Select Processes"
            bind:selectedIds={selectedNextIds}
            label={selectedNextIds.length > 0
              ? setSelectedValues(selectedNextIds, nextToSelect)
              : "Select Processes"}
            items={nextToSelect}
          />
        {/key}
      {:else if (isEditMode && selectedType === "Processes") || (!isEditMode && selectedAddType === "Processes")}
        {#key previousToSelect}
          <!-- Im Projekt-Framework steht das Department nur in der Ueberschrift -->
          {#if !isEditMode && !projectScopeActive}
            <!-- Neuer Process: genau ein Department; API erwartet weiter eine Liste -->
            <ComboBox
              titleText="Select Department"
              placeholder="Search or select a department"
              items={[...previousToSelect].sort((a, b) =>
                a.text.localeCompare(b.text),
              )}
              selectedId={selectedPrevIds[0]}
              shouldFilterItem={filterFrameworkParentOption}
              on:select={(event) =>
                (selectedPrevIds = [event.detail.selectedId])}
              on:clear={() => (selectedPrevIds = [])}
            />
          {:else if isEditMode}
            <!-- Bearbeiten: bestehende Mehrfachzuordnungen nicht beim Speichern verlieren -->
            <MultiSelect
              titleText="Select Departments"
              bind:selectedIds={selectedPrevIds}
              label={selectedPrevIds.length > 0
                ? setSelectedValues(selectedPrevIds, previousToSelect)
                : "Select Departments"}
              items={previousToSelect}
            />
          {/if}
          <!-- <MultiSelect
          titleText="Select Objects"
          bind:selectedIds={selectedNextIds}
          label={selectedNextIds.length > 0
            ? setSelectedValues(selectedNextIds, nextToSelect)
            : "Select Objects"}
          items={nextToSelect}
        /> -->
        {/key}
      {:else if (isEditMode && selectedType === "Subprocesses") || (!isEditMode && selectedAddType === "Subprocesses")}
        {#key previousToSelect}
          <ComboBox
            titleText="Select Process"
            placeholder="Search or select a process"
            items={[...previousToSelect].sort((a, b) =>
              a.text.localeCompare(b.text),
            )}
            bind:selectedId={selectedPrevId}
            shouldFilterItem={filterFrameworkParentOption}
            on:keydown={clearFrameworkParentOnSearch}
          />
        {/key}
      {:else if (isEditMode && selectedType === "Tasks") || (!isEditMode && selectedAddType === "Tasks")}
        {#key previousToSelect}
          <ComboBox
            titleText="Select Subprocess"
            placeholder="Search or select a subprocess"
            items={[...previousToSelect].sort((a, b) =>
              a.text.localeCompare(b.text),
            )}
            bind:selectedId={selectedPrevId}
            shouldFilterItem={filterFrameworkParentOption}
            on:keydown={clearFrameworkParentOnSearch}
          />
        {/key}
      {:else if (isEditMode && selectedType === "Subtasks") || (!isEditMode && selectedAddType === "Subtasks")}
        {#key previousToSelect}
          <ComboBox
            titleText="Select Task"
            placeholder="Search or select a task"
            items={[...previousToSelect].sort((a, b) =>
              a.text.localeCompare(b.text),
            )}
            bind:selectedId={selectedPrevId}
            shouldFilterItem={filterFrameworkParentOption}
            on:keydown={clearFrameworkParentOnSearch}
          />
        {/key}
      {:else}
        {#key previousToSelect}
          <MultiSelect
            titleText={`Select ${
              (isEditMode ? selectedType : selectedAddType) === "Objects"
                ? "Processes"
                : ""
            }`}
            bind:selectedIds={selectedPrevIds}
            label={selectedPrevIds.length > 0
              ? setSelectedValues(selectedPrevIds, previousToSelect)
              : "Select Processes"}
            items={previousToSelect}
          />
        {/key}
      {/if}
      {#if frameworkModalError}<p class="company-missing-path-error">
          {frameworkModalError}
        </p>{/if}
    </div>
  {/if}
  <Button
    slot="left-buttons"
    kind="secondary"
    text="Cancel"
    on:click={() => (openModalAdd = false)}
  />
</Modal>

<DangerModal
  danger
  bind:open={openOrgDangerModal}
  preventCloseOnClickOutside
  modalHeading={`Delete ${getOrgTypeLabel(orgModalType)}`}
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:click:button--primary={deleteOrgElement}
  on:click:button--secondary={() => (openOrgDangerModal = false)}
>
  <div class="deleteModalBody">
    <p>
      Are you sure you want to delete this {getOrgTypeLabel(
        orgModalType,
      )?.toLowerCase()}:
      <span class="project-name">{selectedOrgObject?.name}</span>?
    </p>
  </div>
</DangerModal>

<Modal
  modalHeading={`${orgModalMode === "edit" ? "Edit" : "Add New"} ${getOrgTypeLabel(orgModalType)}`}
  bind:open={openOrgModal}
  actions
  autowidth
  preventCloseOnClickOutside
  noSecondaryButton
  modalClass="framework-element-modal"
  primaryButtonText="Save"
  primaryButtonVariant="secondary"
  on:click:button--secondary={() => (openOrgModal = false)}
  primaryButtonAction={saveOrgElement}
  primaryButtonNotAllow={!orgModalName.trim() ||
    (orgModalType !== "Business-Units" && !orgModalParentId)}
>
  <div class="framework-modal-fields">
    <TextInput
      labelText={`${getOrgTypeLabel(orgModalType)} name`}
      placeholder={`Enter ${getOrgTypeLabel(orgModalType)} name`}
      bind:value={orgModalName}
    />
    {#if orgModalType !== "Business-Units"}
      <Select
        labelText={`Select ${orgModalType === "Areas" ? "Department" : "Business Unit"}`}
        bind:selected={orgModalParentId}
      >
        {#each getOrgParentOptions(orgModalType) as option}
          <SelectItem value={option.id} text={option.name} />
        {/each}
      </Select>
    {/if}
  </div>
  <Button
    slot="left-buttons"
    kind="secondary"
    text="Cancel"
    on:click={() => (openOrgModal = false)}
  />
</Modal>

<ExistingFrameworkDetectedModal
  bind:open={openExistingFrameworkModal}
  on:refresh={refreshTables}
  on:continue={handleContinue}
  {impactSummary}
  {replaceFramework}
/>

<ImportFrameworkModal
  bind:open={openImportModal}
  on:refresh={refreshTables}
  on:submit={handleImportSubmit}
/>

<style>
  .framework-header-actions {
    display: flex;
    align-items: center;
    gap: var(--control-gap);
  }

  :global(.replace-assistant-draft-modal .bx--modal-container) {
    width: min(36rem, calc(100vw - 2rem));
  }

  :global(.replace-assistant-draft-modal .buttons-container) {
    margin-top: 1rem;
  }

  :global(
      .replace-assistant-draft-modal .bx--modal-scroll-content > .modal_wrapper
    ) {
    padding-bottom: 0.5rem !important;
  }

  .replace-assistant-draft-copy {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .replace-assistant-draft-copy p {
    margin: 0;
    line-height: 1.5;
  }

  .main {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  /* Abstand unter dem Intro kommt allein vom gap, sonst addiert sich der margin der Hinweise */
  .project-framework-new {
    gap: var(--control-gap);
    --framework-top-gap: 0;
  }

  .project-framework-new :global(.company-framework-content) {
    min-height: 0;
  }

  :global(.project-framework-wrapper),
  :global(.project-framework-wrapper .view_wrapper),
  :global(.project-framework-wrapper .canvas) {
    height: auto;
  }

  :global(.project-framework-wrapper .canvas) {
    min-height: 0;
  }

  .project-framework-legacy,
  .project-framework-legacy .project-framework-notices {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .project-framework-legacy :global(.warning-notice) {
    margin: 0;
  }

  .project-framework-intro {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    padding: 1rem 1rem 0;
  }

  .project-framework-intro :global(.warning-notice) {
    margin-bottom: 0;
  }

  .project-framework-new {
    gap: var(--control-gap);
    --framework-top-gap: 0;
  }

  .project-framework-legacy,
  .project-framework-legacy .project-framework-notices {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .project-framework-intro {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    padding: 1rem 1rem 0;
  }

  .deleteModalBody {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    padding: 1.5rem 0;
    font-family: Inter;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .deleteModalBody p {
    margin: 0;
    line-height: inherit;
  }

  .deleteModalBody span {
    font-weight: 700;
  }

  .deleteModalBody .delete-warning {
    color: var(--pax-orange);
    font-weight: 700;
  }

  .deleteModalBody .project-name {
    font-weight: bold;
  }

  .delete-impact-group {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .delete-impact-chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--control-gap);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .delete-impact-chips li {
    min-width: 0;
    max-width: 100%;
  }

  .delete-impact-chips :global(.bx--tag) {
    max-width: 100%;
    height: auto;
    margin: 0;
    padding: 0.25rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.5;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .options-container {
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .switcher-wrapper {
    display: flex;
    align-items: flex-start;
    gap: var(--control-gap);
  }

  .switcher-wrapper :global(.grid_container) {
    align-self: flex-start;
    height: 3rem !important;
    min-height: 3rem;
    max-height: 3rem;
  }

  /* .filter-wrapper {
    display: flex;
    align-items: flex-end;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .filter-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: fit-content;
  }

  .filter-label {
    color: var(--pax-black);
    font-size: 0.75rem;
    line-height: 1;
  } */

  .table-container {
    margin: 0;
  }

  .company-missing-path-context {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    margin-bottom: 1rem;
    color: var(--pax-grey);
    font-size: 0.8125rem;
  }

  .company-missing-path-error {
    margin: 0;
    color: var(--pax-danger);
    font-size: 0.8125rem;
  }

  /* Bei einem frueheren Merge verlorene Regeln aus really_unstable (Framework-Element-Dialoge) */
  .framework-modal-fields {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  /* Eigener TextInput bringt margin-top mit; Abstand kommt hier nur vom gap */
  .framework-modal-fields > :global(.text-input) {
    margin-top: 0;
  }

  /* Carbon-Labels (ComboBox/MultiSelect) wie das TextInput-Label; important gegen die globale
     Regel .bx--modal-container .bx--label { margin-top: 2rem !important } */
  .framework-modal-fields :global(.bx--label) {
    margin-top: 0 !important;
    margin-bottom: var(--xs);
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
    letter-spacing: 0.02em;
  }

  /* Gleiche Feldhoehe wie TextInput */
  .framework-modal-fields :global(.bx--list-box),
  .framework-modal-fields :global(.bx--list-box__field),
  .framework-modal-fields :global(.bx--combo-box .bx--text-input) {
    height: 3rem;
    max-height: 3rem;
  }

  /* Aufgeklappte Listen nicht am Modal-Rand abschneiden */
  :global(.framework-element-modal .bx--modal-content) {
    overflow: visible;
  }

  /* Footer-Abstände: oben nur der Divider-Abstand, unten gleich viel inkl. Container-Padding */
  :global(.framework-element-modal .buttons-container) {
    margin-top: 0;
  }

  :global(
      .framework-element-modal
        .bx--modal-scroll-content
        > .modal_wrapper:last-child
    ) {
    padding-bottom: 0.5rem !important;
  }

  /* Carbon-Auswahlfelder (ComboBox, MultiSelect, Select) an eigenen TextInput angleichen */
  :global(.framework-element-modal .bx--label) {
    margin-bottom: var(--xs);
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    line-height: 1rem;
  }

  :global(.framework-element-modal .bx--list-box),
  :global(.framework-element-modal .bx--select-input) {
    height: auto;
    max-height: none;
    border: solid var(--bw) var(--pax-black-inactive);
    border-radius: var(--br);
    background-color: var(--pax-white);
    color: var(--pax-black);
    font-size: 0.875rem;
  }

  :global(.framework-element-modal .bx--list-box:hover),
  :global(.framework-element-modal .bx--select-input:hover) {
    background-color: var(--pax-white);
  }

  :global(.framework-element-modal .bx--list-box:focus-within),
  :global(.framework-element-modal .bx--select-input:focus) {
    outline: solid var(--bw) var(--pax-black);
    outline-offset: 0;
  }

  /* Carbon-Padding rechts bleibt für Clear-/Menü-Icons erhalten */
  :global(.framework-element-modal .bx--list-box .bx--text-input),
  :global(.framework-element-modal .bx--list-box__field) {
    height: auto;
    padding-top: var(--s);
    padding-bottom: var(--s);
    padding-left: var(--s);
    border: none;
    border-radius: var(--br);
    background-color: transparent;
    font-size: 0.875rem;
    line-height: normal;
    color: var(--pax-black);
  }

  :global(.framework-element-modal .bx--select-input) {
    padding-top: var(--s);
    padding-bottom: var(--s);
    padding-left: var(--s);
    line-height: normal;
  }

  :global(.framework-element-modal .bx--list-box .bx--text-input:focus),
  :global(.framework-element-modal .bx--list-box__field:focus) {
    outline: none;
  }

  :global(.framework-element-modal .bx--list-box .bx--text-input::placeholder) {
    color: var(--pax-black-inactive);
  }

  :global(.framework-element-modal .bx--list-box__menu) {
    border-radius: var(--br);
  }
</style>
